// A FILA DO VENDEDOR — na tela ela se chama "Gestao de Vendas" e mora no menu Equipe desde
// 04/09/2026 ([DA-670]); a rota segue /orcamentos/fila ([DA-669]).
//
// Copia a FORMA da fila do estoquista: a linha aparece porque o orcamento esta aberto e some
// quando ele vira venda. Nao ha botao "Ganhou" — a venda tira a linha pelo status ([DA-362]).
//
// Sem placar e sem comparacao entre vendedores: quem nao e' admin ve so a propria fila (o servidor
// decide, nao esta tela). Comparar vendedora em tela publica e' cobranca com outro nome, e o Ruan
// disse que cobranca gera desgaste — a fila existe pra substituir a cobranca, nao pra automatiza-la.
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useNavigate } from 'react-router-dom';
import { BookOpen, EyeOff, MessageCircle, Minus, Pencil, Plus, RefreshCw, Tags, X } from 'lucide-react';
import { contar, formatMoeda, formatTelefoneBR, formatDataHoraSP, formatDataSP, formatHoraSP } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useGridNavegavel } from '../../lib/useGridNavegavel';
import { Avatar } from '../../components/Avatar';
import { Modal } from '../../components/Modal';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { ModalMotivosPerda, type MotivoPerda } from './ModalMotivosPerda';
import { ModalContatosNaoVenda } from './ModalContatosNaoVenda';
import { SeletorPedido } from './SeletorPedido';
import { CirculosCor, TiraPaleta, pinturaDoRotulo, pinturaSeEscolhida, type CampoCor } from './CoresDoRotulo';
import { AgendaVendas, type LinhaAgenda } from './AgendaVendas';
import { ContatosDoDia } from './ContatosDoDia';
import { EstatisticasContatos } from './EstatisticasContatos';
import { ContatosInvalidos } from './ContatosInvalidos';
import { OrigemClientes } from './OrigemClientes';
import { PlacarRecusas } from './PlacarRecusas';
import { PainelNaoQuerReceber } from './PainelNaoQuerReceber';
import { PainelStatusEnvio } from './PainelStatusEnvio';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';
import { BadgeEtiqueta } from '../atendimento/componentes/BadgeEtiqueta';

type Balde = 'cliente' | 'sem_contato';
type Aba = Balde | 'agenda' | 'contatos' | 'base' | 'origem' | 'invalidos' | 'estatisticas' | 'recusas' | 'naoquer' | 'status';

// Exportada so' pro `SeletorPedido`, que devolve uma linha inteira pro modal de Andamento.
// ⚠️ `import type` la, nunca import de valor: o ciclo existe (esta tela importa aquele campo) e so'
// nao vira ciclo em tempo de execucao porque o tipo e' apagado na compilacao.
export interface LinhaFila {
  id: number;
  numero: string;
  total: string;
  dias_parado: number;
  whatsapp_enviado: string | null;
  retomada_status: string | null;
  retomada_quando: string | null;
  retomada_em: string | null;
  situacao: string | null;
  motivo: string | null;
  situacao_cor_fundo: string | null;
  situacao_cor_fonte: string | null;
  motivo_cor_fundo: string | null;
  motivo_cor_fonte: string | null;
  contato_id: number | null;
  /** Foto do perfil do WhatsApp, do sincronizador de 6h. Nula quando o numero nunca teve foto. */
  foto_url: string | null;
  /** O cliente foi perguntado e recusou dar o telefone (migration 0277). */
  telefone_recusado: boolean;
  /** Data da ultima compra deste cliente, por cadastro ou pelo numero. Nula quando nao ha. */
  ultima_compra: string | null;
  /** Se este orcamento virou venda: o numero (VND-) e o instante REAL do caixa. */
  venda_numero: string | null;
  venda_em: string | null;
  vendedora: string | null;
  cliente: string | null;
  itens: number;
  /** Quantos flys foram pro contato na janela da fila ([DA-682] Fase 2). Zero sem numero. */
  flys: number;
  /** Etiquetas visiveis do numero (qualquer ficha dele), com cor e apelido vindos da rota. */
  etiquetas?: Array<{ nome: string; cor: string | null; apelido: string | null }>;
}

interface SituacaoAndamento { id: number; nome: string; cor_fundo?: string | null; cor_fonte?: string | null }

/** Um passo da trajetoria do cliente ([DA-675]). O servidor ja devolve tudo em ordem de tempo. */
interface EventoTrajetoria {
  tipo: 'pedido' | 'perda' | 'andamento';
  quando: string;
  /** So no andamento: a hora MARCADA pro proximo contato. O `quando` e' quando ela REGISTROU. */
  marcado_para: string | null;
  orcamento_id: number;
  numero: string;
  total: string;
  rotulo: string;
  cor_fundo: string | null;
  cor_fonte: string | null;
  comentario: string | null;
  quem: string | null;
  enviado_em: string | null;
}

/** Um fly mandado pro contato na janela ([DA-682] Fase 2). */
interface FlyEnviado {
  criado_em: string;
  quem: string | null;
  de_promocao: boolean;
  /** Os produtos da arte, por nome. Nulo se nenhum id sobreviveu (fly automatico antigo). */
  produtos: string | null;
}

interface Trajetoria {
  eventos: EventoTrajetoria[];
  /** O "quais" do pedido dele — a coluna da tabela responde so o "quantos". */
  flys: FlyEnviado[];
  cliente: string | null;
  /** false = o numero e' da casa (separacao), entao a gaveta mostra so este pedido. */
  do_cliente: boolean;
  total_pedidos: number;
  ganhos: number;
  /** A janela da fila, pra gaveta ESCREVER o prazo em vez de cravar "7". */
  dias: number;
}

// As duas caixas de desfecho (Perdeu e Andamento) carregam a MESMA forma: o rotulo em cima, a data
// na linha de baixo (pedido dele, 04/09/2026, com print das duas colunas). Peca unica de proposito
// — duas implementacoes do mesmo formato viram disfarce em vez de conferencia ([DA-591] p.7).
// Vazia enquanto ninguem registrou nada: a moldura da caixa e' o que diz que ali se clica.
function RotuloComData({ rotulo, data }: { rotulo: string | null; data: string | null }) {
  if (!rotulo) return null;
  return (
    <span className="flex flex-col items-center leading-tight">
      <span className="font-semibold">{rotulo}</span>
      {/* `opacity` e nao uma cor cinza: a fonte aqui e' escolhida por ele ([DA-674]) e pode ser
          clara ou escura — cinza cravado sumiria em metade dos pares. Opacidade se apoia na
          propria cor, entao a data acompanha qualquer escolha. */}
      {data && <span className="text-[10px] opacity-75">{data}</span>}
    </span>
  );
}

/**
 * A GAVETA DA TRAJETORIA ([DA-675]) — tudo que ja foi tratado com aquele cliente.
 *
 * Pedido dele em 04/09/2026: *"clicando nele o sistema mostraria toda a tragetoria de contatos com
 * o cliente. Dessa forma ficaria facil clicar e entender o que ja tratado com o cliente"*.
 *
 * ⛔ **Nenhum pedido daqui e' clicavel, de proposito.** Abrir orcamento REAJUSTA o preco ao cadastro
 * vivo ([DA-124], que ele reafirmou no mesmo dia) — uma lista de 15 numeros clicaveis seria 15
 * chances de regravar valor de pedido antigo num gesto que a pessoa faz pra LER.
 *
 * ⚠️ **O resumo do topo e' o que responde "quem e' este cliente"** — foi o ganho que apareceu ao
 * medir: os andamentos tinham 6 horas de vida, e quem ja tinha massa era o historico de pedidos
 * (clientes da fila com 15, 11 e 10 desde maio). Sem ele a gaveta seria uma lista sem veredito.
 */
function GavetaTrajetoria({ dados }: { dados: Trajetoria | null | undefined }) {
  // `undefined` nao acontece na pratica (o toggle semeia `null` antes de abrir), mas tratar os dois
  // como "carregando" evita a gaveta piscar vazia se a ordem mudar algum dia.
  if (!dados) {
    return <div className="px-8 py-3 text-xs text-gray-500" style={{ backgroundColor: 'var(--page-bg)' }}>Carregando a trajetoria...</div>;
  }

  // O evento mais antigo responde "desde quando ele compra aqui" — a lista ja vem em ordem de
  // tempo, entao e' o ultimo. Em variavel por causa do `noUncheckedIndexedAccess`.
  const maisAntigo = dados.eventos[dados.eventos.length - 1];

  return (
    <div className="px-8 py-3" style={{ backgroundColor: 'var(--page-bg)' }}>
      <div className="text-xs mb-2 flex flex-wrap items-baseline gap-x-2" style={{ color: 'var(--text-muted)' }}>
        {dados.do_cliente ? (
          <>
            <strong style={{ color: 'var(--num-destaque)' }}>{contar(dados.total_pedidos, 'pedido')}</strong>
            {/* "0 viraram venda" e' gramatica de contador, nao de gente — a tela publicada mostrou
                isso e nao havia como ver antes. Zero merece a palavra, nao o algarismo. */}
            <span>· {dados.ganhos === 0
              ? 'nenhum virou venda'
              : `${dados.ganhos} ${dados.ganhos === 1 ? 'virou' : 'viraram'} venda`}</span>
            {maisAntigo && <span>· desde {formatDataSP(maisAntigo.quando)}</span>}
          </>
        ) : (
          // ⚠️ Gaveta curta PRECISA dizer por que e' curta: sem esta linha ela le como "este cliente
          // nunca comprou aqui", que e' uma afirmacao falsa sobre um numero que e' da propria loja.
          <span>So este pedido — o numero e' da loja (separacao), nao de um cliente.</span>
        )}
      </div>

      {/* OS FLYS — o "quais" do pedido dele ([DA-682] Fase 2). Bloco proprio, e nao evento na
          linha do tempo abaixo, porque as duas listas tem JANELAS diferentes: a trajetoria nao
          tem prazo (vai ate o primeiro pedido, maio pra alguns clientes) e o fly tem os N dias da
          fila. Misturados, um fly de 20 dias faltaria no MEIO da lista sem nada dizer que foi
          cortado — ausencia que nao se distingue de inexistencia ([DA-468]).
          ⚠️ A caixa so nasce quando ha fly. Ela nao aparece vazia porque o selo da coluna Flys ja
          responde "nao houve" pela ausencia — repetir aqui seria a mesma informacao duas vezes na
          mesma tela, que ele ja mandou tirar antes ([DA-500]). */}
      {dados.flys.length > 0 && (
        <div
          className="rounded-lg mb-2 px-3 py-1.5"
          style={{ border: '1px solid var(--input-border, #e5e7eb)', backgroundColor: 'var(--panel-bg)' }}
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wide mb-1" style={{ color: 'var(--text-muted)' }}>
            {/* A janela vai ESCRITA e vem do servidor: cravar "7" mentiria no dia em que ele
                mudar a config da fila, e o cabecalho da tela ja anuncia o mesmo prazo. */}
            ✦ {contar(dados.flys.length, 'fly', 'flys')} nos ultimos {dados.dias} dias
          </div>
          {dados.flys.map((f, i) => (
            <div key={`fly-${f.criado_em}-${i}`} className="flex items-baseline gap-3 text-xs py-0.5">
              <span className="tabular-nums flex-shrink-0" style={{ color: 'var(--text-muted)', width: 96 }}>
                {formatDataSP(f.criado_em)} {formatHoraSP(f.criado_em)}
              </span>
              {f.de_promocao && (
                <span className="flex-shrink-0 rounded px-1.5 font-semibold" style={{ backgroundColor: '#fde68a', color: '#000', fontSize: 10 }}>
                  promocao
                </span>
              )}
              {/* ⚠️ Fly sem produto nao e' erro de leitura: o disparo automatico ate 08/08/2026
                  gravava `metadata` VAZIA, e ali nao ha id nenhum pra resolver em nome. Dizer isso
                  e' melhor que uma linha muda, que se leria como defeito da tela. */}
              <span className="flex-1 min-w-0 truncate" title={f.produtos ?? undefined}>
                {f.produtos || <span style={{ color: 'var(--text-muted)' }}>produto nao registrado (fly automatico antigo)</span>}
              </span>
              {f.quem && <span className="flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{f.quem}</span>}
            </div>
          ))}
        </div>
      )}

      {dados.eventos.length === 0 ? (
        <div className="text-xs text-gray-500">Nada registrado ainda.</div>
      ) : (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--input-border, #e5e7eb)', backgroundColor: 'var(--panel-bg)' }}>
          {dados.eventos.map((ev, i) => (
            <div
              key={`${ev.tipo}-${ev.orcamento_id}-${ev.quando}-${i}`}
              className="flex items-baseline gap-3 px-3 py-1.5 text-xs"
              style={{ borderTop: i === 0 ? 'none' : '1px solid var(--page-bg)' }}
            >
              <span className="tabular-nums flex-shrink-0" style={{ color: 'var(--text-muted)', width: 96 }}>
                {formatDataSP(ev.quando)} {formatHoraSP(ev.quando)}
              </span>
              {/* O rotulo veste a MESMA cor da coluna Andamento / Perdeu ([DA-674]): quem pintou
                  AGUARDANDO de laranja reconhece o laranja aqui sem precisar reler a palavra. */}
              <span
                className="rounded px-1.5 py-0.5 font-semibold flex-shrink-0"
                style={{
                  minWidth: 92, textAlign: 'center',
                  ...(ev.cor_fundo || ev.cor_fonte
                    ? pinturaSeEscolhida(ev.cor_fundo, ev.cor_fonte)
                    : { border: '1px solid var(--input-border, #d1d5db)', color: 'var(--text-muted)' }),
                }}
              >
                {ev.rotulo}
              </span>
              <span className="tabular-nums flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{ev.numero}</span>
              <span className="tabular-nums font-semibold flex-shrink-0">{formatMoeda(Number(ev.total))}</span>
              <span className="flex-1 min-w-0 truncate" title={ev.comentario ?? undefined}>
                {ev.comentario || ''}
                {/* A hora MARCADA so existe no andamento, e ela e' futuro — por isso vem etiquetada
                    em vez de solta: solta, seria lida como a hora em que o contato aconteceu. */}
                {ev.marcado_para && (
                  // ⚠️ `formatDataSP` + `formatHoraSP`, nunca `formatDataHoraSP` — este ultimo traz
                  // os SEGUNDOS (`08:00:00`), que nao dizem nada num compromisso e comem a linha.
                  // E' o mesmo par que a coluna Andamento ja usa, entao as duas leem igual.
                  <span style={{ color: 'var(--text-muted)' }}>
                    {ev.comentario ? ' · ' : ''}proximo contato {formatDataSP(ev.marcado_para)} {formatHoraSP(ev.marcado_para)}
                  </span>
                )}
              </span>
              {ev.quem && <span className="flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{ev.quem}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// O rotulo diz PRA ONDE o orcamento foi enviado. Era "Pro cliente" e o proprio Ruan leu como
// "Por cliente", ou seja "agrupado por cliente", que a lista nao faz: o mesmo telefone aparece em
// mais de uma linha. "Enviados ao cliente" carrega o eixo sozinho (08/09/2026).
//
// ⛔ **A aba "Separados sem venda" saiu em 08/09/2026**, junto com a decisao de esconder de vez o
// que vai pro celular da propria vendedora. Ela era alimentada pela MESMA lista de numeros que
// agora exclui — sem fonte, so' restaria uma aba permanentemente vazia.
/** Os meses do seletor das Estatisticas, que desde a [DA-703] e' desenhado no cabecalho da pagina. */
const MESES_ESTAT = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const ABAS: { id: Balde; label: string }[] = [
  { id: 'cliente', label: 'Enviados ao cliente' },
  { id: 'sem_contato', label: 'Sem contato' },
];

// Cada aba diz o RECORTE que ela mostra. Sem isso o contador vira um numero sem dono e a
// vendedora nao tem como saber por que um pedido esta ali (molde da Conferencia).
const RECORTE: Record<Aba, string> = {
  // ⚠️ A dica do duplo-clique vive AQUI, no texto que a aba sempre mostra — e nao so' no aviso de
  // semana vazia, que some assim que existe um compromisso. Recurso que so' se descobre por acaso
  // e' recurso que nao existe ([DA-667]).
  agenda:
    'Os andamentos marcados, na semana. Cada compromisso aparece na hora escolhida; as horas vazias ficam de proposito, porque e nelas que da pra ver onde cabe um contato novo — clique duas vezes numa hora livre pra marcar dali mesmo. Quem nao e admin ve so os proprios.',
  // ⚠️ O recorte diz o CRITERIO, e aqui ele precisa dizer mais: a tela de Estatisticas dos Agentes
  // tem um badge com o MESMO nome ("Contatos do dia") que conta outra coisa — so' quem virou
  // chamado ([DA-103]), o que da' um numero menor. Nome igual com destino diferente ja custou caro
  // ([DA-604]); aqui o texto e' o que separa os dois.
  contatos:
    'Todo numero que escreveu pra caixa Atendimento hoje, que RECEBEU mensagem nossa hoje (inclusive fly) ou que comprou hoje, tenha virado chamado ou nao — quem so comprou entra pela hora da venda, sem contagem de mensagem, e quem so recebeu entra sem hora nenhuma na coluna Recebidas, porque ele nao mandou nada: o horario dele esta em "Ult. envio". Venda de balcao sem contato nenhum nao aparece, porque aqui a linha e o contato. Serve pra conferir se o contato ja esta categorizado: quem esta sem etiqueta aparece primeiro, e da pra marcar por aqui mesmo; depois deles a lista vem agrupada por etiqueta e, dentro de cada grupo, por nome. Numero cadastrado em "Nao sao venda" nao entra. A coluna ORIGEM diz por onde a pessoa apareceu HOJE: CHAT quando escreveu, recebeu o orcamento pelo WhatsApp ou foi so procurada por nos, BALCAO quando comprou sem passar pelo chat — quem fez as duas coisas conta como CHAT, e amanha a mesma pessoa pode aparecer pelo outro lado. A coluna do meio e quem cuidou do contato no chat, ou o vendedor quando nao houve atendimento; quem comprou HOJE aparece tambem com o numero do orcamento e o total — venda de outro dia nao conta aqui, e o vendedor so aparece quando vendeu outra pessoa. O selo ✦ mostra o fly que a loja mandou pro contato, com o item e a data, na mesma janela de dias da fila. O botao Estatistica abre o mapa do dia: quantos escreveram, quantos a loja procurou (a primeira mensagem do dia foi nossa), quantos receberam fly, quem ficou sem resposta, contato novo, quem respondeu o fly no mesmo dia e quem comprou — clicar num quadro deixa na lista so aqueles contatos. Resposta automatica do WhatsApp do cliente nao conta como ele ter escrito.',
  // ⚠️ **O recorte precisa dizer QUEM FICOU DE FORA**, e este e' o caso em que mais importa: sao
  // A agenda importada APARECE por padrao desde 15/10/2026 (decisao dele, [DA-814]); ate ali ficava
  // escondida ([DA-705]). O texto diz como tira-la, porque ela e' mais de um terco da lista.
  base:
    'Todo contato da caixa Atendimento que ja teve alguma relacao com a loja: escreveu, recebeu mensagem nossa, ou comprou. As colunas sao as mesmas da aba "Contatos do dia", com a janela trocada — a data e a da ULTIMA mensagem que ele mandou, a contagem e o TOTAL de mensagens dele, a venda e a ULTIMA compra (com a data), e o selo ✦ e o ULTIMO fly, sem limite de dias. A lista ja abre INCLUINDO a agenda de aparelho importada em 28/03/2026 (contatos que nunca escreveram, nunca receberam e nunca compraram) — desmarque "Incluir a agenda importada" pra ver apenas quem teve relacao com a loja. O lapis ao lado do Sobrenome edita nome, sobrenome e TELEFONE; o numero novo passa pela conferencia do WhatsApp antes de gravar. Numero cadastrado em "Nao sao venda" nao entra, e grupos tambem nao. Serve pra ver como anda a base: quantos existem e quantos ainda estao sem etiqueta, com o filtro "So os sem etiqueta" pra trabalhar a fila e marcar por aqui mesmo. A ordem e a mesma da aba do dia: quem esta SEM etiqueta primeiro, depois agrupado por etiqueta, e dentro de cada grupo por nome. 50 por pagina.',
  // ⚠️ Diz o que e' contado (COMPRADOR, nao venda) e de onde vem a resposta — senao a pizza se le
  // como "de onde vieram as vendas", e quem compra muito pesaria mais que quem veio uma vez.
  origem:
    'De onde vieram os compradores do mes, pela resposta de "Como conheceu a loja?" no Enviar ao Caixa. Cada cliente conta uma vez, pela origem gravada no cadastro dele, nao importa quantas vezes comprou; venda sem cadastro (quem nao quis informar o telefone) conta por venda. Quem comprou sem resposta fica no numero de cima, fora da pizza. As opcoes se cadastram em Arquivos > Cadastros Auxiliares > Origem do Cliente.',
  invalidos:
    'Contatos da caixa Atendimento para quem o WhatsApp NAO entrega: grupos, listas de transmissao, numero gravado errado (repetido duas vezes ou dois numeros colados, quase todos da agenda importada em 28/03/2026) e numero que o WhatsApp recusou num envio de campanha. Nenhum deles recebe fly. A coluna Status diz o motivo e o que fazer: o que tem conserto vem primeiro — o lapis troca o numero (conferido no WhatsApp antes de gravar) e, quando o numero so esta repetido, o campo ja abre com o numero certo. Grupo e lista so se apagam, pela lixeira, que mostra antes o que vai junto.',
  // ⚠️ O recorte diz o denominador ANTES do numero aparecer: a media e' de dia util e ignora dia
  // sem movimento, senao os 19 dias de caixa fora do ar de agosto/2026 fariam o mes parecer uma
  // queda de movimento que nunca houve ([DA-692]).
  // ⚠️ **Curto de proposito, e a razao foi MEDIDA** ([DA-703]): em 14px este paragrafo chegou a
  // 102px e empurrava o grafico pra baixo da dobra na janela dele. O detalhe que estava aqui
  // (o que a conta cobre, quando os flys comecaram) vive no rodape do proprio grafico, onde e'
  // lido junto com o desenho — aqui fica so' o RECORTE, que e' o que muda a leitura do numero.
  estatisticas:
    'O mapa do dia, somado no mes: os mesmos quadros da aba Contatos do dia, e o grafico mostra o quadro escolhido dia a dia. Sabado, domingo e caixa fora do ar mexem mais no numero do que qualquer acao de marketing.',
  // ⚠️ O recorte desta aba tem que dizer que ela NAO e' lista de trabalho, e que a recusa nao e'
  // falha de quem vendeu — senao o numero e' lido como cobranca de erro em vez de pergunta.
  recusas:
    'As vendas que sairam com a recusa registrada: o cliente foi perguntado e nao quis dar o telefone. Nao e falha de quem vendeu — mas o percentual de cada um mostra quando a pergunta parou de ser feita. Sao vendas ja fechadas, entao esta lista nao esvazia: ela existe pra ser conferida, nao trabalhada.',
  // ⚠️ O recorte tem que separar esta aba da lista vizinha ANTES de qualquer outra coisa: pelo
  // nome, "Nao quer receber" e "Nao sao venda" parecem a mesma lista, e a diferenca e' inteira —
  // uma esconde quem NAO e' cliente, a outra guarda quem E' cliente e so' nao quer oferta.
  naoquer:
    'Os clientes que pediram pra nao receber promocao — respondendo "Nao quero" no WhatsApp, ou incluidos aqui pela loja. Nenhum fly sai pra eles: nem o automatico, nem o que a vendedora manda no chat, nem disparo por etiqueta. ATENCAO: eles CONTINUAM clientes normais e aparecem em todas as outras abas, comprando e sendo atendidos como sempre — quem some da Gestao inteira e "Nao sao venda", que e outra lista, no botao do cabecalho. Esta aqui nao esvazia: existe pra conferir por que o botao de material apareceu desligado numa conversa.',
  // ⚠️ O recorte diz o que FICA DE FORA (o fly do chat) e o limite do cinza — sem isso o grupo
  // "Chegou" se le como "nao leu", e medido em 24/09/2026 havia gente ali que tinha RESPONDIDO.
  status:
    'Os flys das CAMPANHAS nos ultimos dias da janela, separados pelo que o WhatsApp informou: nao saiu (numero recusado), saiu mas nao chegou ao celular (um check), chegou (dois checks cinza) e leu (dois checks azuis). O fly que a vendedora manda pelo chat nao entra aqui. ATENCAO: dois checks cinza nao provam que a pessoa nao leu — quem desliga a confirmacao de leitura no WhatsApp nunca manda o azul. Clique no + de cada status pra ver os contatos.',
  cliente:
    'Orcamentos abertos que foram enviados pro WhatsApp de um cliente e ainda nao viraram venda. Some sozinho quando o pedido vai pro caixa. Numero cadastrado em "Nao sao venda" nao entra aqui.',
  sem_contato:
    'Orcamentos que nunca sairam da loja: nao ha numero pra retomar. Ficam a vista pra tela nao fingir que a semana teve menos pedido em aberto do que teve.',
};

const VAZIO: Record<Balde, string> = {
  cliente: 'Nenhum orcamento de cliente esperando retorno. Quando um pedido ficar aberto, ele aparece aqui no dia seguinte.',
  sem_contato: 'Todo orcamento aberto da janela tem um numero. Bom sinal.',
};

// Idade em selo, sem verde/vermelho (daltonismo): cinza hoje, amarelo da casa nos primeiros dias,
// laranja quando passa de 4 — que e onde o historico mostra a venda praticamente morrendo.
function seloIdade(dias: number): { texto: string; fundo: string } {
  if (dias <= 0) return { texto: 'hoje', fundo: '#e2e8f0' };
  if (dias <= 3) return { texto: `${dias} ${dias === 1 ? 'dia' : 'dias'}`, fundo: '#fde68a' };
  return { texto: `${dias} dias`, fundo: '#f97316' };
}

export function FilaRetomada() {
  const navigate = useNavigate();
  const toast = useToast();
  // ⚠️ O que veio do `localStorage` e' VALIDADO, nunca aceito como esta. Quem tinha a aba
  // 'separacao' salva (removida em 08/09/2026) abriria a tela numa aba que nao existe mais: as
  // abas todas apagadas e a lista mostrando outro balde, sem nada explicando. Aba salva que nao
  // esta na lista de hoje volta pra 'cliente'.
  // A ajuda da aba, no livrinho ([DA-703]). `fixed` ancorado no botao — a posicao e' lida no
  // clique, senao ela envelhece quando a pagina rola.
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [ajudaPos, setAjudaPos] = useState({ top: 0, right: 0 });
  // ⛔ Mes/ano das Estatisticas moram AQUI desde 09/09/2026 (pedido dele: *"retire Mes e Ano daqui
  // e passe para cima"*, pra ganhar altura): os seletores sao desenhados no cabecalho da pagina,
  // que ja existe e tinha espaco vazio, e o valor desce por prop. ⚠️ Estado unico — duplicar em
  // dois lugares faria o seletor e o grafico discordarem em silencio.
  const hojeRef = new Date();
  const [estatMes, setEstatMes] = useState(hojeRef.getMonth() + 1);
  const [estatAno, setEstatAno] = useState(hojeRef.getFullYear());

  const [aba, setAba] = useState<Aba>(() => {
    const salvo = localStorage.getItem('fila-retomada:aba');
    const validas: string[] = [...ABAS.map((a) => a.id), 'agenda', 'contatos', 'base', 'origem', 'invalidos', 'estatisticas', 'recusas', 'naoquer', 'status'];
    return (salvo && validas.includes(salvo) ? salvo : 'cliente') as Aba;
  });
  const [linhas, setLinhas] = useState<LinhaFila[]>([]);
  // ⛔ Nasce DESLIGADO e nao guarda no localStorage: sao 176 convertidos por semana contra 64
  // abertos, e a aba "Sem contato" iria de 41 pra 174 linhas. Uma preferencia lembrada faria a
  // vendedora abrir a tela de trabalho ja afogada, sem lembrar por que.
  const [mostrarConvertidos, setMostrarConvertidos] = useState(false);
  const [contadores, setContadores] = useState({ cliente: 0, sem_contato: 0 });
  const [dias, setDias] = useState(7);
  const [soMeu, setSoMeu] = useState(false);
  const [carregando, setCarregando] = useState(true);
  // `linha: null` = veio do duplo-clique na Agenda, e o pedido e' escolhido dentro da propria caixa.
  // `andamentoId` presente = veio do lapis do cartao, e a caixa CORRIGE em vez de criar.
  const [desfecho, setDesfecho] = useState<{
    linha: PedidoDoDesfecho | null;
    tipo: 'perdido' | 'adiado';
    andamentoId?: number;
    situacaoId?: number | null;
    comentario?: string | null;
  } | null>(null);
  const [gestaoMotivos, setGestaoMotivos] = useState(false);
  const [gestaoNaoVenda, setGestaoNaoVenda] = useState(false);
  // O duplo-clique na Agenda ([DA-684]): a celula da' o DIA e a HORA, e o PEDIDO e' escolhido
  // dentro da propria caixa de Andamento. Isto so' carrega o dia/hora ate la.
  const [agendaPrefill, setAgendaPrefill] = useState<{ data: string; hora: string } | null>(null);
  // Cada gravacao incrementa isto, e a Agenda (que carrega sozinha, por semana) se rele.
  const [sinalRecarga, setSinalRecarga] = useState(0);
  // A TRAJETORIA de cada linha aberta ([DA-675]). `null` no mapa = pedida e ainda em voo, que e' o
  // que deixa a gaveta dizer "carregando" em vez de piscar vazia.
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [trajetorias, setTrajetorias] = useState<Record<number, Trajetoria | null>>({});
  // A foto do contato ampliada. Guarda a URL e o nome (vira legenda) porque a linha some do
  // `linhas` quando a fila recarrega, e o lightbox nao pode piscar no meio da leitura.
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; nome: string } | null>(null);
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const podeVerMotivos = ehAdmin || usuario?.perfil === 'gerente';

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      // A Agenda tem carregamento proprio (janela de semana, nao balde) — aqui so os contadores
      // das abas, senao os numeros congelariam enquanto ela estivesse aberta.
      // A Agenda e os Contatos do dia tem carregamento proprio (uma le por semana, a outra por
      // dia) — aqui so' os contadores das abas, senao os numeros congelariam enquanto uma delas
      // estivesse aberta.
      if (aba === 'agenda' || aba === 'contatos' || aba === 'base' || aba === 'origem' || aba === 'invalidos' || aba === 'estatisticas' || aba === 'recusas' || aba === 'naoquer' || aba === 'status') {
        setContadores(await api.get<{ cliente: number; sem_contato: number }>('/api/fila-retomada/resumo'));
        setLinhas([]);
        return;
      }
      const [fila, resumo] = await Promise.all([
        api.get<{ linhas: LinhaFila[]; dias: number; so_meu: boolean }>('/api/fila-retomada', {
          query: { balde: aba, ...(mostrarConvertidos ? { convertidos: '1' } : {}) },
        }),
        api.get<{ cliente: number; sem_contato: number }>('/api/fila-retomada/resumo'),
      ]);
      // ⚠️ `itens` e `flys` chegam como NUMERO porque a rota os casta com `::int` — sem o cast,
      // `count(*)` e' bigint e o driver o entrega como string, e o `contar()` erra o plural
      // calado ("1 itens"). O conserto mora no SQL, nao aqui; ver o comentario na rota.
      setLinhas(fila.linhas);
      setDias(fila.dias);
      setSoMeu(fila.so_meu);
      setContadores(resumo);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao carregar a fila');
    } finally {
      setCarregando(false);
    }
    // `mostrarConvertidos` entra aqui porque ele muda o QUE a rota devolve: sem a dependencia o
    // `useCallback` congelaria o valor antigo e marcar a caixa nao traria linha nenhuma.
  }, [aba, mostrarConvertidos, toast]);

  useEffect(() => { void carregar(); }, [carregar]);
  useEffect(() => { localStorage.setItem('fila-retomada:aba', aba); }, [aba]);
  // ⛔ As duas abas de ADMIN (pedido dele em 08/09/2026; a de contatos nasceu admin+gerente e foi
  // fechada no mesmo dia). O perfil so' se conhece DEPOIS do lazy init do `useState`, entao a
  // correcao vem aqui: quem tiver uma delas guardada e nao puder ve-la abriria numa aba
  // inexistente, com as abas apagadas e a lista de outro balde na tela — o mesmo beco que a
  // 'separacao' removida ja tinha aberto. ⚠️ A trava de verdade e' a do SERVIDOR; esta so' evita
  // a tela sem saida.
  useEffect(() => {
    if ((aba === 'contatos' || aba === 'base' || aba === 'origem' || aba === 'invalidos' || aba === 'estatisticas' || aba === 'recusas' || aba === 'naoquer' || aba === 'status') && !ehAdmin) setAba('cliente');
  }, [aba, ehAdmin]);

  // Abrir guarda QUAL registro estava sendo trabalhado. Fica no localStorage, e nao no state do
  // navigate, porque o caminho de volta nao e' so a seta do orcamento: F5, o voltar do navegador e
  // reabrir a tela mais tarde tem que cair no mesmo lugar.
  const abrir = useCallback((l: LinhaFila) => {
    localStorage.setItem('fila-retomada:ultimo', String(l.id));
    navigate(`/orcamentos/${l.id}`, { state: { from: '/orcamentos/fila' } });
  }, [navigate]);

  // O botao de WhatsApp ([DA-672]) — abre a conversa daquele cliente DENTRO do Atendimento, com a
  // seta de voltar pra ca.
  //
  // ⚠️ **Nao e' um link `wa.me`.** Pelo WhatsApp de fora a conversa acontece no aparelho dela e
  // nao deixa rastro nenhum no sistema: a Gestao de Vendas nao fica sabendo que houve contato, o
  // Andamento nao e' alimentado e a conversa some da caixa da loja. E, mais direto: dentro do
  // WhatsApp nao ha como por um botao de voltar, que foi o que ele pediu.
  const abrirWhatsApp = useCallback((telefone: string, nome: string | null, orcamentoId: number) => {
    localStorage.setItem('fila-retomada:ultimo', String(orcamentoId));
    const q = new URLSearchParams({
      telefone,
      inbox: 'atendimento',
      voltar: '/orcamentos/fila',
      rotulo: 'Gestao de Vendas',
    });
    if (nome) q.set('nome', nome);
    navigate(`/atendimento?${q.toString()}`);
  }, [navigate]);

  // Abre e fecha a gaveta da trajetoria ([DA-675]). Busca sob demanda e guarda: reabrir a mesma
  // linha nao gasta chamada, e a lista da fila nao carrega historico de 50 pedidos por linha.
  //
  // ⚠️ O fetch fica FORA do updater do `setState` — dentro dele nada roda na hora (o updater e'
  // executado no render seguinte), que e' a armadilha ja escrita no `sistema/CLAUDE.md`.
  const alternarTrajetoria = useCallback((id: number) => {
    setExpandidos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
      return proximo;
    });
    setTrajetorias((prev) => {
      if (prev[id] !== undefined) return prev;
      void api.get<Trajetoria>(`/api/fila-retomada/${id}/trajetoria`)
        .then((t) => setTrajetorias((p) => ({ ...p, [id]: t })))
        .catch(() => setTrajetorias((p) => ({
          ...p,
          [id]: { eventos: [], flys: [], cliente: null, do_cliente: false, total_pedidos: 0, ganhos: 0, dias: 7 },
        })));
      return { ...prev, [id]: null };
    });
  }, []);

  // Trocar de aba fecha as gavetas: as linhas sao outras, e gaveta aberta numa linha que saiu da
  // lista reabriria sozinha se um id se repetisse entre baldes.
  useEffect(() => { setExpandidos(new Set()); }, [aba]);

  // `ativo: !desfecho` — com o modal aberto o Enter e' dele, senao a tecla abriria o orcamento atras.
  const grid = useGridNavegavel<LinhaFila>({ itens: linhas, getId: (l) => l.id, onAtivar: abrir, ativo: !desfecho });
  const { setSelectedId } = grid;

  // Ao voltar, a linha de onde ele saiu vem marcada e o hook rola ate ela. Semeia UMA vez por aba
  // carregada: depois disso quem manda e' o clique/seta do operador, senao recarga automatica
  // arrastaria a selecao dele de volta.
  const semeouRef = useRef<Aba | null>(null);
  useEffect(() => {
    if (carregando || linhas.length === 0 || semeouRef.current === aba) return;
    semeouRef.current = aba;
    const salvo = localStorage.getItem('fila-retomada:ultimo');
    const id = salvo ? Number(salvo) : NaN; // string vazia viraria 0 no Number direto
    if (Number.isFinite(id) && linhas.some((l) => l.id === id)) setSelectedId(id);
  }, [linhas, carregando, aba, setSelectedId]);

  const soma = linhas.reduce((s, l) => s + Number(l.total), 0);

  return (
    <div className="p-4">
      <div className="pagina-header">
        <h1 className="pagina-titulo">Gestao de Vendas</h1>
        <div className="flex items-center gap-3">
          {/* ⚠️ **Mes/ano das Estatisticas vivem AQUI** ([DA-703], pedido dele: *"retire Mes e Ano
              daqui e passe para cima"*). Nao e' arrumacao: eles ocupavam uma linha inteira DENTRO da
              aba, e essa aba tem ~354px uteis com os avisos do topo abertos — o cabecalho da pagina
              ja existia e tinha espaco vazio a esquerda de "ultimos N dias", que e' exatamente onde
              ele desenhou o retangulo. ⛔ So' aparecem na aba que os usa; nas outras seriam controle
              sem efeito, que e' pior que controle nenhum. */}
          {/* A aba Origem tambem le por MES, entao usa o mesmo par de seletores. */}
          {(aba === 'estatisticas' || aba === 'origem') && (
            <div className="flex items-center gap-2">
              <select value={estatMes} onChange={(e) => setEstatMes(Number(e.target.value))} className="text-sm">
                {MESES_ESTAT.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select value={estatAno} onChange={(e) => setEstatAno(Number(e.target.value))} className="text-sm">
                {[hojeRef.getFullYear(), hojeRef.getFullYear() - 1].map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
          )}
          {/* ⛔ **Fora da aba "Contatos"**: la nao ha janela de dias — a lista e' o acervo inteiro
              ([DA-705]). Deixar "ultimos 7 dias" no cabecalho de uma tela que mostra contato de
              maio nao e' enfeite, e' uma afirmacao FALSA sobre o que esta na tela. */}
          {/* Nem na Origem, que le pelo mes escolhido ao lado — "ultimos N dias" ali seria falso. */}
          {aba !== 'base' && aba !== 'origem' && (
            <span className="text-xs text-gray-500">
              ultimos {dias} dias{soMeu ? ' · so os seus' : ''}
            </span>
          )}
          {podeVerMotivos && (
            <button type="button" onClick={() => setGestaoMotivos(true)} className="btn-secundario flex items-center gap-1 text-sm">
              <Tags size={14} /> Motivos de perda
            </button>
          )}
          {/* ⚠️ Vive AQUI, e nao em Configuracoes, pelo mesmo motivo do "Motivos de perda": o
              cadastro E' o relatorio, e ele so' faz sentido ao lado da tela que ele governa —
              esconder pedido de uma tela e' um ato que precisa ser conferivel de dentro dela. */}
          {podeVerMotivos && (
            <button type="button" onClick={() => setGestaoNaoVenda(true)} className="btn-secundario flex items-center gap-1 text-sm">
              <EyeOff size={14} /> Nao sao venda
            </button>
          )}
          {/* ⚠️ Traz de volta o que JA VIROU VENDA (pedido dele, 08/09/2026). Ate aqui a linha
              sumia no instante da conversao e a vendedora nunca via que deu certo — some igual a
              "evaporou". Fica FORA da aba Agenda, que le por semana e nao por balde. */}
          {/* ⛔ Fora da aba "Contatos" tambem: ela lista CONTATO, e este filtro governa ORCAMENTO —
              a rota da base nem le o parametro. Controle sem efeito e' pior que controle nenhum. */}
          {/* ⛔ E fora da aba "Status de Envio": ela lista FLY de campanha, nao orcamento. */}
          {aba !== 'agenda' && aba !== 'base' && aba !== 'status' && aba !== 'origem' && (
            <label
              className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
              title="Traz tambem os orcamentos que ja viraram venda, com a data. Sao 176 por semana — por isso nasce desligado."
            >
              <input
                type="checkbox"
                checked={mostrarConvertidos}
                onChange={(e) => setMostrarConvertidos(e.target.checked)}
                className="w-4 h-4 cursor-pointer"
              />
              Mostrar convertidos em venda
            </label>
          )}
          {/* ⛔ **Este botao e' o UNICO Atualizar da tela desde 09/09/2026** ([DA-704]). As abas que
              carregam sozinhas (Agenda por semana, Contatos do dia por dia, Estatisticas por mes,
              Recusas por periodo) nao sao alcancadas pelo `carregar()` daqui — ele sai cedo e busca
              so' os contadores. Por isso cada uma tinha o SEU botao, e na tela isso virava dois
              `Atualizar` identicos a 114px de distancia com efeitos diferentes, o de cima mudo.
              O `setSinalRecarga` e' o que fecha a distancia. ⛔ Ele sobe AQUI, no clique — nao
              dentro do `carregar()`, que roda tambem ao trocar de aba ([DA-684]). */}
          <button
            type="button"
            onClick={() => { void carregar(); setSinalRecarga((n) => n + 1); }}
            className="btn-secundario flex items-center gap-1 text-sm"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </div>

      <div className="abas">
        {ABAS.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setAba(a.id)}
            className={`aba-btn ${aba === a.id ? 'aba-btn-ativo' : ''}`}
          >
            {a.label} ({contadores[a.id]})
          </button>
        ))}
        {/* A Agenda e' a quarta aba e NAO leva contador: as tres primeiras contam o mesmo universo
            (pedido aberto na janela), ela mostra outro (compromisso marcado numa semana). Um numero
            ali seria comparado com os vizinhos e nao fala a mesma lingua. */}
        <button
          type="button"
          onClick={() => setAba('agenda')}
          className={`aba-btn ${aba === 'agenda' ? 'aba-btn-ativo' : ''}`}
        >
          Agenda
        </button>
        {/* Sem contador, pela mesma razao da Agenda: as duas primeiras contam ORCAMENTO na janela
            de dias e esta conta CONTATO no dia. Numero aqui seria lido como se falasse a mesma
            lingua dos vizinhos. O placar do que falta etiquetar vive dentro da aba ([DA-691]). */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('contatos')}
            className={`aba-btn ${aba === 'contatos' ? 'aba-btn-ativo' : ''}`}
          >
            Contatos do dia
          </button>
        )}
        {/* A base inteira ([DA-705]). Sem contador pelo mesmo motivo das vizinhas — ela conta
            CONTATO no acervo e as primeiras contam ORCAMENTO na janela de dias. E o placar que
            importa aqui (quantos faltam etiquetar) vive dentro da aba, junto do filtro que o
            trabalha. */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('base')}
            className={`aba-btn ${aba === 'base' ? 'aba-btn-ativo' : ''}`}
          >
            Contatos
          </button>
        )}
        {/* Entre Contatos e Invalidos, onde ele a pediu (16/10/2026, com print). */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('origem')}
            className={`aba-btn ${aba === 'origem' ? 'aba-btn-ativo' : ''}`}
          >
            Origem
          </button>
        )}
        {/* Ao lado de Contatos, onde ele a pediu (15/10/2026). Sem contador, como as vizinhas: o
            total e o placar por status ficam dentro da aba. */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('invalidos')}
            className={`aba-btn ${aba === 'invalidos' ? 'aba-btn-ativo' : ''}`}
          >
            Invalidos
          </button>
        )}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('estatisticas')}
            className={`aba-btn ${aba === 'estatisticas' ? 'aba-btn-ativo' : ''}`}
          >
            Estatisticas
          </button>
        )}
        {/* Sem contador no rotulo, pelo mesmo motivo das duas acima: as primeiras contam ORCAMENTO
            ABERTO na janela de dias e esta conta VENDA FECHADA no periodo escolhido dentro da aba.
            Numero aqui seria lido como se falasse a mesma lingua dos vizinhos ([DA-697]). */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('recusas')}
            className={`aba-btn ${aba === 'recusas' ? 'aba-btn-ativo' : ''}`}
          >
            Nao quis informar
          </button>
        )}
        {/* Sem contador tambem, pelo mesmo motivo das vizinhas: as primeiras contam ORCAMENTO
            ABERTO e esta conta PESSOA que saiu das promocoes — numero aqui seria lido como se
            falasse a mesma lingua ([DA-697]). O total vai dentro da aba, com o rotulo junto. */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('naoquer')}
            className={`aba-btn ${aba === 'naoquer' ? 'aba-btn-ativo' : ''}`}
          >
            Nao quer receber
          </button>
        )}
        {/* Sem contador, como as vizinhas: esta conta FLY DE CAMPANHA, nao orcamento. */}
        {ehAdmin && (
          <button
            type="button"
            onClick={() => setAba('status')}
            className={`aba-btn ${aba === 'status' ? 'aba-btn-ativo' : ''}`}
          >
            Status de Envio
          </button>
        )}

        {/* O livrinho fica na PONTA DIREITA da barra de abas, onde ele o desenhou. `ml-auto` empurra
            e nao mexe no alinhamento das abas. */}
        <button
          type="button"
          className="ml-auto flex items-center gap-1"
          aria-label="O que esta aba mostra"
          aria-expanded={ajudaAberta}
          title="O que esta aba mostra"
          style={{ color: 'var(--panel-fg)', padding: '2px 6px' }}
          onClick={(e) => {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setAjudaPos({ top: Math.round(r.bottom + 6), right: Math.round(window.innerWidth - r.right) });
            setAjudaAberta((v) => !v);
          }}
        >
          <BookOpen size={18} />
        </button>
      </div>

      {/* ⚠️ **O texto saiu da tela e virou BALAO no livrinho** (pedido dele em 09/09/2026, [DA-703]:
          *"este texto deve ficar dentro do icone de um livrinho no lado direito da tela, ao clicar
          nele e exibido um balao"*). O motivo e' altura: em 14px o paragrafo comia 61 a 102px de
          uma aba que tem ~354px uteis com os avisos do topo abertos.
          ⛔ **Nao foi apagado** — este e' o RECORTE, o que diz o que o numero cobre e o que ele
          esconde; sem ele a tela vira numero sem denominador ([DA-692]). Ele so' deixou de ocupar
          espaco permanente pra quem ja o leu.
          ⚠️ O balao e' `fixed` ancorado no botao, nunca `absolute`: dentro de um container que rola
          o `absolute` sai cortado, que e' a regra da casa pros dropdowns. */}
      {ajudaAberta && (
        <>
          <div className="fixed inset-0" style={{ zIndex: 69 }} onClick={() => setAjudaAberta(false)} />
          <div
            className="fixed rounded-xl border shadow-lg p-3"
            style={{
              zIndex: 70, top: ajudaPos.top, right: ajudaPos.right, maxWidth: 560,
              backgroundColor: 'var(--panel-bg)', borderColor: 'var(--input-border, #d1d5db)',
              color: 'var(--panel-fg)', fontSize: 14,
            }}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <strong style={{ fontSize: 14 }}>O que esta aba mostra</strong>
              <button type="button" onClick={() => setAjudaAberta(false)} aria-label="Fechar"
                      style={{ color: 'var(--panel-fg)' }}>
                <X size={16} />
              </button>
            </div>
            {RECORTE[aba]}
          </div>
        </>
      )}

      {aba === 'status' ? (
        <PainelStatusEnvio sinalRecarga={sinalRecarga} />
      ) : aba === 'invalidos' ? (
        <ContatosInvalidos sinalRecarga={sinalRecarga} />
      ) : aba === 'origem' ? (
        <OrigemClientes mes={estatMes} ano={estatAno} sinalRecarga={sinalRecarga} />
      ) : aba === 'naoquer' ? (
        <PainelNaoQuerReceber ehAdmin={ehAdmin} />
      ) : aba === 'recusas' ? (
        <PlacarRecusas sinalRecarga={sinalRecarga} />
      ) : aba === 'estatisticas' ? (
        <EstatisticasContatos mes={estatMes} ano={estatAno} sinalRecarga={sinalRecarga} />
      ) : aba === 'contatos' || aba === 'base' ? (
        /* ⚠️ **A MESMA peca nas duas abas, so' muda a janela** ([DA-705]) — o `key` obriga o React
            a remontar ao trocar de aba: sem ele, ir de "Contatos do dia" pra "Contatos" reusaria a
            instancia e a lista do dia ficaria na tela enquanto a base carrega, o que se le como se
            a aba nova tivesse trazido os contatos errados. */
        <ContatosDoDia
          key={aba}
          modo={aba === 'base' ? 'base' : 'dia'}
          sinalRecarga={sinalRecarga}
          onAbrirConversa={(telefone, nome) => {
            const q = new URLSearchParams({
              telefone,
              inbox: 'atendimento',
              voltar: '/orcamentos/fila',
              rotulo: 'Gestao de Vendas',
              nome,
            });
            navigate(`/atendimento?${q.toString()}`);
          }}
        />
      ) : aba === 'agenda' ? (
        <AgendaVendas
          onAbrirOrcamento={(id) => {
            localStorage.setItem('fila-retomada:ultimo', String(id));
            navigate(`/orcamentos/${id}`, { state: { from: '/orcamentos/fila' } });
          }}
          sinalRecarga={sinalRecarga}
          onWhatsApp={(l: LinhaAgenda) => abrirWhatsApp(l.whatsapp_enviado!, l.cliente, l.orcamento_id)}
          // Uma caixa so' ([DA-684]): o duplo-clique abre direto o modal de Andamento, com o dia e
          // a hora da celula preenchidos e o pedido como primeiro campo — sem escala intermediaria.
          onMarcar={(data, hora) => {
            setAgendaPrefill({ data, hora });
            setDesfecho({ linha: null, tipo: 'adiado' });
          }}
          // ⚠️ O dia e a hora saem do `quando` JA GRAVADO, lidos no fuso da loja pelos helpers do
          // `shared` — nao de `new Date(...).getHours()`, que le no fuso de quem abriu a tela e
          // reabriria o compromisso das 09:00 como 06:00 pra quem estiver noutro fuso.
          onEditar={(l) => {
            setAgendaPrefill({ data: formatDataSP(l.quando).split('/').reverse().join('-'), hora: formatHoraSP(l.quando) });
            setDesfecho({
              linha: { id: l.orcamento_id, numero: l.numero, cliente: l.cliente, total: l.total },
              tipo: 'adiado',
              andamentoId: l.id,
              situacaoId: l.situacao_id,
              comentario: l.comentario,
            });
          }}
        />
      ) : (
      /* `overflow-x-auto`, nunca `hidden`: com o piso de 1630px a tabela nao cabe em tela
          estreita, e o `hidden` cortaria as ultimas colunas CALADO — sem barra pra alcancar. */
      <div className="rounded-xl overflow-x-auto border border-gray-200" style={{ backgroundColor: 'var(--panel-bg)' }}>
        {carregando ? (
          <div className="text-center text-gray-400 py-12 text-sm">Carregando...</div>
        ) : linhas.length === 0 ? (
          <div className="text-center py-12 px-6">
            <div className="font-semibold mb-1">Nada por aqui</div>
            <div className="text-sm text-gray-500 max-w-lg mx-auto">{VAZIO[aba]}</div>
          </div>
        ) : (
          <>
            {/* `fixed` e' o que faz a largura declarada valer de verdade: no algoritmo automatico
                ela e' so uma sugestao e a sobra infla tudo (a coluna de 112 aparecia com 148, e a
                mesma coluna mudava de tamanho a cada aba). Com `fixed`, o que esta escrito e' o que
                sai na tela, e o Cliente absorve o resto. */}
            {/* 110 + 70 + 126 (WhatsApp) + 5x180 = 1206 de largura fixa, mais o Cliente, que
                absorve o resto. Em tela estreita isso nao cabe, entao a tabela tem piso e a moldura
                rola — sem o piso, o layout `fixed` encolheria as cinco proporcionalmente e os 180
                deixariam de valer.
                ⚠️ O piso reserva 200px pro Cliente: medido no tablet (1024), sem essa reserva quem
                absorve a sobra fica com as migalhas (74px) e o nome quebra em TRES linhas. */}
            {/* 1324 = 70 (Parado) + 200 (piso do Cliente, que absorve a sobra) + 154 (WhatsApp —
                `w-44` e' 11rem = **154px**, nao 176, porque a raiz do app e' 14px) + 5x180.
                Eram 1434 ate a coluna Orcamento da esquerda sair: os 110px dela viraram folga na
                janela dele (~1520 uteis, com o navegador em zoom 125%). */}
            {/* 1358 = os 1324 de antes + 34 da coluna do "+" ([DA-675]). Segue cabendo nos ~1520
                uteis da janela dele (zoom 125%), com 162px de folga. */}
            {/* 1402 = os 1358 + 44 da coluna da FOTO (pedido dele, 08/09/2026, com o print
                marcando o lugar: entre Parado e Cliente). Medido injetando a coluna na tabela de
                producao antes de existir codigo: na janela de 1900 do print dele a tabela **nao
                cresceu** (1815px antes e depois) — os 44px sairam do Cliente, a unica coluna sem
                largura fixa (609 -> 565), sem nenhum nome quebrando em duas linhas, e a altura da
                linha ficou nos mesmos 46px. A 1520 a folga cai de 162 pra 118px. */}
            <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 1402 }}>
              <thead className="sticky top-0" style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
                <tr>
                  {/* ⛔ A coluna Orcamento da ESQUERDA saiu em 04/09/2026, a pedido dele. Ela
                      dizia exatamente o que a coluna de abrir passou a dizer — dois cabecalhos com
                      o mesmo nome e o mesmo numero na mesma linha ([DA-500]: duas coisas dizendo a
                      mesma coisa). De quebra devolveu 110px, que e' a folga que faltava na janela
                      dele ([DA-671]). */}
                  {/* A coluna do "+" ([DA-675]). Sem titulo: o cabecalho nomeia o que a coluna
                      CONTEM, e aqui o conteudo e' o gesto de abrir a gaveta. */}
                  <th className="px-1 py-2 font-medium w-[34px]" aria-label="Trajetoria" />
                  <th className="text-left px-3 py-2 font-medium w-20">Parado</th>
                  {/* A foto do contato, sem cabecalho escrito: o rotulo nao acrescenta nada a uma
                      coluna de 44px que se explica sozinha, e gastaria a largura do nome ao lado. */}
                  <th className="px-1 py-2 font-medium w-[44px]" aria-label="Foto do contato" />
                  {/* ⛔ MESMA sequencia de colunas nas TRES abas (pedido dele, 04/09/2026, com o
                      cabecalho e as abas marcadas no print). Antes a `separacao` trocava Cliente
                      por Itens e o WhatsApp so existia em `cliente`: as colunas trocavam de lugar
                      ao alternar de aba, e o olho perdia a referencia a cada clique.
                      Sem largura de proposito: no layout `fixed` alguma coluna precisa absorver a
                      sobra, senao ela infla TODAS proporcionalmente. O Cliente e' o lugar certo —
                      e' o texto mais longo e o que mais varia. */}
                  <th className="text-left px-3 py-2 font-medium">Cliente</th>
                  {/* ETIQUETA — pedido dele em 25/09/2026; coluna propria e nao embaixo do nome,
                      escolha dele: colada no nome a pill cai numa abcissa diferente em cada linha
                      (a licao da aba Contatos do dia, [DA-691]). Empilhadas ([DA-737]).
                      136 = o selo mais largo (AR CONDICIONADO: 100 de texto + 12 do selo) + 24 da
                      celula; com 124 ele quebrava em duas linhas dentro do proprio selo. */}
                  <th className="text-left px-3 py-2 font-medium w-[136px]">Etiqueta</th>
                  {/* w-44 (=154px, nao 176: `w-*` e' rem e a raiz do app e' 14px), no lugar de
                      w-36 (=126). O botao de WhatsApp entrou na frente do numero e comeu 22px mais
                      o respiro; com 126 o telefone passava a truncar. */}
                  <th className="text-left px-3 py-2 font-medium w-44">WhatsApp</th>
                  {/* FLYS — pedido dele em 07/09/2026, com print marcando o lugar: DEPOIS da
                      coluna WhatsApp ([DA-682] Fase 2). A coluna responde "quantos"; o "quais"
                      (produto e data) mora na gaveta do "+", porque nome de produto nao cabe aqui.
                      ⚠️ 48px medidos na TELA PUBLICADA, injetando a coluna e comparando antes e
                      depois: a tabela NAO cresce (segue 1435 de 1520) porque os 48 saem da unica
                      coluna sem largura fixa, a Cliente (277 -> 229). Custo a 1520: 3 dos 20 nomes
                      passam a quebrar em 2 linhas (+4px). Na janela de 1900 do print dele, Cliente
                      vai a 609 e NENHUMA linha cresce. Derivar isso da aritmetica erraria — e' a
                      licao da [DA-648]. */}
                  <th className="text-center px-3 py-2 font-medium w-[48px]">Flys</th>
                  {/* Cinco colunas iguais (pedido dele, 04/09/2026, com print): cada botao ganhou
                      coluna propria — antes os tres dividiam uma celula so.
                      ⚠️ 180, nao 250: com 250 a tabela pedia 1760px e a tela dele tem ~1520 uteis
                      (o navegador esta em zoom 125%), entao a ULTIMA coluna caia fora da vista e o
                      botao "Falar" so aparecia rolando. Com 180 a tabela inteira cabe. Folgado pro
                      conteudo: o maior botao tem 65px e o maior valor ~134 com o respiro.
                      ⚠️ Em 25/09/2026 as tres primeiras deixaram de ser iguais (escolha dele): a
                      coluna Etiqueta deixou o Cliente com 61px na janela de 1536 (zoom 125%).
                      Medido na tela publicada: o maior valor tem 77px, a maior vendedora 49 e o
                      botao do Orcamento 96 — e o espaco voltou pro nome. ⛔ Perdeu e Andamento
                      FICAM em 180: a caixa com "NAO TINHAMOS O PRODUTO" ocupa ~179. */}
                  <th className="text-right px-3 py-2 font-medium w-[130px]">Valor</th>
                  <th className="text-left px-3 py-2 font-medium w-[125px]">Vendedora</th>
                  {/* Pedido dele, 04/09/2026: a coluna que abria o pedido ganha titulo e passa a
                      mostrar o NUMERO no lugar de "Abrir" — o numero diz qual pedido se esta
                      abrindo, "Abrir" nao dizia nada. */}
                  <th className="text-center px-3 py-2 font-medium w-[140px]">Orcamento</th>
                  {/* As tres colunas de acao nomeadas (pedido dele, 04/09/2026, com os cabecalhos
                      marcados em dois prints). O conteudo segue sendo o BOTAO com o rotulo — o
                      titulo diz do que a coluna trata, o botao continua sendo o que se aperta. */}
                  <th className="text-center px-3 py-2 font-medium w-[180px]">Perdeu</th>
                  <th className="text-center px-3 py-2 font-medium w-[180px]">Andamento</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => {
                  const selo = seloIdade(l.dias_parado);
                  const rp = grid.rowProps(l);
                  const aberta = expandidos.has(l.id);
                  return (
                    <Fragment key={l.id}>
                    <tr
                      {...rp}
                      className="border-b border-gray-100"
                      style={{
                        backgroundColor: idx % 2 === 0 ? 'var(--grid-row-bg)' : 'var(--grid-row-alt-bg)',
                        color: 'var(--grid-row-fg)',
                        ...rp.style,
                      }}
                    >
                      {/* ⚠️ `stopPropagation` no duplo-clique tambem: sem ele, dois cliques rapidos
                          pra abrir e fechar a gaveta ATIVARIAM a linha e abririam o orcamento — e
                          abrir orcamento reajusta preco ([DA-124]), o que nao pode sair de um
                          gesto de leitura. */}
                      <td className="px-1 py-2 text-center" onDoubleClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); alternarTrajetoria(l.id); }}
                          title={aberta ? 'Fechar a trajetoria' : `Ver o que ja foi tratado com ${l.cliente ?? 'este cliente'}`}
                          aria-label={aberta ? 'Fechar a trajetoria' : 'Ver a trajetoria do cliente'}
                          aria-expanded={aberta}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: 4, padding: 0,
                            border: '1px solid var(--input-border, #d1d5db)',
                            background: aberta ? 'var(--accent, #2563eb)' : 'transparent',
                            color: aberta ? '#fff' : 'var(--accent, #2563eb)',
                            cursor: 'pointer',
                          }}
                        >
                          {aberta ? <Minus size={12} /> : <Plus size={12} />}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ backgroundColor: selo.fundo, color: '#000' }}>
                          {selo.texto}
                        </span>
                      </td>
                      {/* ⚠️ Sem nome E sem foto a celula fica VAZIA, nao com um "?" — e' o caso
                          das linhas anonimas da aba sem contato, onde a bolinha de interrogacao
                          repetida seria enfeite que nao identifica ninguem. */}
                      {/* ⚠️ `stopPropagation` no duplo-clique pelo mesmo motivo do botao da
                          gaveta: dois cliques pra ampliar e fechar a foto ATIVARIAM a linha e
                          abririam o orcamento, e abrir orcamento reajusta preco ([DA-124]). */}
                      <td className="px-1 py-2" style={{ textAlign: 'center' }} onDoubleClick={(e) => e.stopPropagation()}>
                        {(l.foto_url || l.cliente)
                          ? <Avatar
                              nome={l.cliente ?? '?'}
                              fotoUrl={l.foto_url}
                              tamanho={32}
                              className="mx-auto"
                              onFotoClick={l.foto_url ? () => setFotoAmpliada({ url: l.foto_url!, nome: l.cliente ?? 'Contato' }) : undefined}
                            />
                          : null}
                      </td>
                      {/* A contagem de itens era COLUNA PROPRIA na aba de separacao e sumiria ao
                          unificar as colunas — e' justamente ali que ela identifica o pedido, onde
                          o "cliente" e' o contato da propria loja e nao diz nada (a coluna Vendedora
                          ao lado ja repete o nome). Volta colada ao nome, em cinza, sem gastar uma
                          coluna. Sem nome nenhum, ela ocupa a linha sozinha. */}
                      <td className="px-3 py-2">
                        {l.cliente
                          ? <>{l.cliente} <span className="text-gray-400 text-xs">· {contar(l.itens, 'item')}</span></>
                          : <span className="text-gray-400">{contar(l.itens, 'item')}</span>}
                        {/* ⚠️ A recusa aparece AQUI, colada ao nome, e nao numa coluna: a tabela ja
                            tem 11 colunas e disputa largura ([DA-683]) — e este selo s'o existe em
                            parte das linhas, entao uma coluna propria ficaria vazia na maioria.
                            ⛔ Ele NAO tira a linha do placar (decisao dele, 08/09/2026): diz por
                            que ela esta sem numero, nao a esconde. */}
                        {/* ⚠️ A ultima compra vai ABAIXO do nome, dentro desta celula — lugar que
                            ele apontou com um retangulo no print (08/09/2026), depois de pedir
                            coluna e eu medir que ela nao cabe: a 1520 (a janela dele, zoom 125%)
                            uma coluna de 110px espremia o Cliente de 565 pra 75px e quebrava 27
                            dos 41 nomes. Aqui custa zero de largura.
                            ⚠️ Sem data, NAO escreve nada: metade das linhas nao tem compra ligada
                            (14 de 27, medido) e um "—" repetido em 13 linhas s'o suja a coluna que
                            carrega o nome. Aqui o silencio nao confunde ninguem — quem quer saber
                            se o cliente ja comprou tem a ficha do contato, que diz explicitamente
                            "sem compra registrada". */}
                        {/* ⚠️ O desfecho BOM, em verde-escuro sobre o fundo claro da linha. Nao usa
                            o par verde/vermelho pra dizer "bom/ruim" ([[user_daltonismo]]): quem
                            informa e' a PALAVRA ("virou venda"), a cor s'o acompanha. */}
                        {l.venda_em && (
                          <div className="text-[11px] mt-0.5 font-medium" style={{ color: '#15803d' }}>
                            virou venda: {formatDataSP(l.venda_em)}
                            {l.venda_numero ? ` · ${l.venda_numero}` : ''}
                          </div>
                        )}
                        {l.ultima_compra && (
                          <div className="text-[11px] text-gray-500 mt-0.5">
                            última compra: {formatDataSP(l.ultima_compra)}
                          </div>
                        )}
                        {l.telefone_recusado && (
                          <div className="text-[11px] mt-0.5" style={{ color: '#b45309' }}>
                            nao quis informar o telefone
                          </div>
                        )}
                        {/* ⛔ O selo "voltou · SITUACAO" SAIU daqui em 04/09/2026: a caixa da
                            coluna Andamento passou a mostrar a mesma situacao, e duas coisas
                            dizendo a mesma coisa na mesma linha e' o que ele ja mandou tirar
                            antes ([DA-500]). A linha que voltou segue se anunciando — pela caixa
                            preenchida, que e' onde a informacao agora mora. */}
                      </td>
                      <td className="px-3 py-2">
                        {l.etiquetas?.length ? (
                          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
                            {l.etiquetas.map((e) => (
                              <BadgeEtiqueta key={e.nome} nome={e.nome} cor={e.cor ?? undefined} apelido={e.apelido} />
                            ))}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                        )}
                      </td>
                      {/* O botao de WhatsApp vai NA FRENTE do telefone (pedido dele, 04/09/2026).
                          ⚠️ Nasce em TODA linha que tem numero — inclusive na aba de separacao,
                          onde eu o tinha barrado. A restricao era minha e estava errada: ali o
                          numero e' o de quem SEPAROU, e o proprio texto da aba diz que "quem sabe
                          quem e' o cliente e' quem separou" — falar com ela era justamente a acao
                          que faltava naquela aba. Guarda que se disfarca de zelo remove recurso.
                          Na "Sem contato" nao ha numero, entao nao ha botao — nao por regra, por
                          falta de destino. */}
                      <td className="px-3 py-2 text-xs" style={{ color: 'var(--num-destaque)' }}>
                        {l.whatsapp_enviado ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); abrirWhatsApp(l.whatsapp_enviado!, l.cliente, l.id); }}
                              title={`Abrir a conversa de ${l.cliente ?? l.numero} no Atendimento`}
                              aria-label={`Abrir a conversa de ${l.cliente ?? l.numero} no Atendimento`}
                              className="rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: '#25d366', color: '#ffffff', border: 'none', width: 22, height: 22 }}
                            >
                              <MessageCircle size={13} />
                            </button>
                            {/* Azul escuro pedido por ele. Token, nao hex: `--num-destaque` e'
                                azul escuro nos temas claros e azul CLARO nos escuros — cravar o
                                `#1e3a8a` deixaria o telefone ilegivel no modo noturno. */}
                            <span className="truncate font-semibold">{formatTelefoneBR(l.whatsapp_enviado)}</span>
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      {/* O selo do fly. Amarelo com fonte PRETA — o par de destaque positivo da
                          casa, o unico que ele distingue ([[user_daltonismo]]).
                          ⛔ Cor CRAVADA, nunca `var(--...)` nas duas pontas: fundo e fonte de
                          familias diferentes de tema se movem juntas e nada garante contraste
                          entre elas — foi como a etiqueta de UF saiu em 2,7:1 ([DA-678]).
                          ⚠️ Linha sem fly nao ganha "0" nem tracinho: zero repetido em 20 linhas
                          e' ruido que come justamente o sinal que a coluna existe pra dar. */}
                      <td className="px-1 py-2 text-center" onDoubleClick={(e) => e.stopPropagation()}>
                        {l.flys > 0 ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); alternarTrajetoria(l.id); }}
                            title={`${contar(l.flys, 'fly', 'flys')} pra ${l.cliente ?? 'este contato'} nos ultimos ${dias} dias — clique pra ver quais`}
                            aria-label={`Ver os ${l.flys} flys enviados`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 2,
                              backgroundColor: '#fde68a', color: '#000000',
                              border: 'none', borderRadius: 999, padding: '1px 7px',
                              fontSize: 10.5, fontWeight: 700, cursor: 'pointer', lineHeight: 1.5,
                            }}
                          >
                            ✦ {l.flys}
                          </button>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">{formatMoeda(Number(l.total))}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{l.vendedora || '—'}</td>
                      {/* Um botao por COLUNA (pedido dele com print). Centralizado: numa coluna de
                          250px um botao de 55 encostado na borda encosta no divisor da vizinha. */}
                      {/* O NUMERO e' o botao de abrir. Segue `btn-secundario` porque tem dois
                          pares ao lado (Perdeu, Andamento) — e' deles que vem o contraste que faz
                          o conjunto ser lido como botao ([DA-667]). */}
                      <td className="px-3 py-2 text-center">
                        <button type="button" title={`Abrir ${l.numero}`} className="btn-secundario text-xs px-2 py-1" style={{ whiteSpace: 'nowrap' }} onClick={(e) => { e.stopPropagation(); abrir(l); }}>
                          {l.numero}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {/* ⚠️ O BOTAO fica; quem some e' o verbo dentro dele. Quem nomeia a coluna
                            e' o CABECALHO (pedido dele, 04/09/2026), entao o botao carrega o que
                            foi PREENCHIDO — como o da coluna Orcamento, que mostra o numero e nao
                            a palavra "Abrir". Vazio enquanto ninguem registrou; a moldura da
                            caixa e' o que diz que ali se clica. */}
                        <button
                          type="button"
                          title={`Marcar ${l.numero} como perdida e registrar o motivo`}
                          className="btn-secundario text-xs px-2 py-1"
                          style={{
                            whiteSpace: 'nowrap', minWidth: 132, minHeight: 26,
                            // So pinta a caixa PREENCHIDA: caixa vazia colorida afirmaria um
                            // desfecho que ninguem registrou.
                            ...(l.retomada_status === 'perdido'
                              ? pinturaSeEscolhida(l.motivo_cor_fundo, l.motivo_cor_fonte)
                              : {}),
                          }}
                          onClick={(e) => { e.stopPropagation(); setDesfecho({ linha: l, tipo: 'perdido' }); }}
                        >
                          {/* O perdido FICA na lista desde a [DA-673] — ate a [DA-672] ele saia no
                              instante da marcacao, e quem marcava via a linha sumir. Aqui aparece
                              o motivo escolhido e o dia em que foi marcado. */}
                          <RotuloComData
                            rotulo={l.retomada_status === 'perdido' ? l.motivo : null}
                            data={l.retomada_em ? formatDataSP(l.retomada_em) : null}
                          />
                        </button>
                      </td>
                      {/* "Andamento" desde 04/09/2026 ([DA-672]) — era "Falar". O rotulo mudou
                          junto com o conteudo: nao e' mais so uma data pra falar de novo, e' a
                          SITUACAO do pedido (reagendado / em negociacao / aguardando) com dia,
                          hora e comentario. "Falar" descreveria so um dos tres. */}
                      <td className="px-3 py-2 text-center">
                        {/* Aqui o preenchido APARECE: o pedido adiado volta pra fila na data
                            marcada, e quando volta a caixa mostra em que pe ele estava
                            (REAGENDADO / NEGOCIACAO / AGUARDANDO). Vazia enquanto nada foi
                            registrado. */}
                        <button
                          type="button"
                          title={l.retomada_status === 'adiado' && l.retomada_quando
                            ? `${l.situacao} · falar em ${formatDataHoraSP(l.retomada_quando)} — clique pra mudar`
                            : `Registrar o andamento de ${l.numero}`}
                          className="btn-secundario text-xs px-2 py-1"
                          style={{
                            whiteSpace: 'nowrap', minWidth: 132, minHeight: 26,
                            ...(l.retomada_status === 'adiado'
                              ? pinturaSeEscolhida(l.situacao_cor_fundo, l.situacao_cor_fonte)
                              : {}),
                          }}
                          onClick={(e) => { e.stopPropagation(); setDesfecho({ linha: l, tipo: 'adiado' }); }}
                        >
                          <RotuloComData
                            rotulo={l.retomada_status === 'adiado' ? l.situacao : null}
                            data={l.retomada_quando ? `${formatDataSP(l.retomada_quando)} ${formatHoraSP(l.retomada_quando)}` : null}
                          />
                        </button>
                      </td>
                    </tr>
                    {aberta && (
                      <tr>
                        {/* ⚠️ DOZE colunas: as 8 originais, a do "+", a de Flys ([DA-682] Fase 2),
                            a da FOTO (08/09/2026) e a de Etiqueta (25/09/2026). Com um numero a menos a gaveta para antes da
                            ultima coluna e deixa um vazio a direita — defeito que so o print
                            mostra, porque a medicao de largura passa verde do mesmo jeito
                            ([DA-675] p.9).
                            Coluna nova na tabela = conferir todo `colSpan` que ja existia. */}
                        <td colSpan={12} style={{ padding: 0 }}>
                          <GavetaTrajetoria dados={trajetorias[l.id]} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="text-xs text-gray-500 px-3 py-2 border-t border-gray-200" style={{ backgroundColor: 'var(--grid-row-alt-bg)' }}>
              {linhas.length} {linhas.length === 1 ? 'orcamento' : 'orcamentos'} · {formatMoeda(soma)} em jogo
            </div>
          </>
        )}
      </div>
      )}

      {desfecho && (
        <ModalDesfecho
          linha={desfecho.linha}
          tipo={desfecho.tipo}
          ehAdmin={ehAdmin}
          dataInicial={agendaPrefill?.data}
          horaInicial={agendaPrefill?.hora}
          andamentoId={desfecho.andamentoId}
          situacaoInicial={desfecho.situacaoId}
          comentarioInicial={desfecho.comentario}
          onFechar={() => { setDesfecho(null); setAgendaPrefill(null); }}
          // ⛔ **O `setSinalRecarga` e' o conserto de 08/09/2026, e ele vive AQUI — no gravou —, nao
          // dentro do `carregar`.** A Agenda tem carregamento proprio (por semana, nao por balde),
          // entao o `carregar()` desta tela nunca a alcancava: gravar mostrava o toast de sucesso e
          // deixava o cartao velho desenhado. Ele leu isso como "nao esta salvando" e regravou o
          // mesmo comentario duas vezes — as duas gravaram, medido no log e no banco.
          // ⚠️ No `carregar` seria pior que no lugar nenhum: ele roda tambem ao TROCAR de aba, e a
          // grade recarregaria duas vezes so' pra abrir, piscando "Carregando..." sem motivo.
          onGravado={() => {
            setDesfecho(null);
            setAgendaPrefill(null);
            setSinalRecarga((n) => n + 1);
            void carregar();
          }}
        />
      )}

      {fotoAmpliada && (
        <LightboxImagem
          imagens={[{ url: fotoAmpliada.url, legenda: fotoAmpliada.nome }]}
          onFechar={() => setFotoAmpliada(null)}
        />
      )}

      {gestaoMotivos && <ModalMotivosPerda ehAdmin={ehAdmin} onFechar={() => setGestaoMotivos(false)} />}
      {/* Fechar recarrega a fila: cadastrar ou reativar um numero muda o que a lista mostra, e a
          tela nao pode continuar exibindo o pedido que a pessoa acabou de mandar esconder. */}
      {gestaoNaoVenda && (
        <ModalContatosNaoVenda
          ehAdmin={ehAdmin}
          onFechar={() => { setGestaoNaoVenda(false); void carregar(); }}
        />
      )}
    </div>
  );
}

/**
 * O que a caixa de desfecho precisa saber do pedido — quatro campos, e nao a `LinhaFila` inteira.
 *
 * ⚠️ Existe porque a Agenda tambem abre esta caixa (pra editar um compromisso) e la o que ha e' uma
 * linha da grade, nao uma linha da fila. Pedir a `LinhaFila` completa obrigaria a inventar `flys`,
 * `dias_parado` e mais dez campos so' pra satisfazer o tipo — dado fabricado pra passar no
 * compilador e' o que vira numero errado na tela depois. A `LinhaFila` satisfaz esta forma sozinha.
 */
interface PedidoDoDesfecho {
  id: number;
  numero: string;
  cliente: string | null;
  total: string;
}

function ModalDesfecho({ linha, tipo, ehAdmin, dataInicial, horaInicial, andamentoId, situacaoInicial, comentarioInicial, onFechar, onGravado }: {
  /**
   * O pedido, ou `null` quando o compromisso nasceu de um duplo-clique na Agenda — ali a celula
   * disse o QUANDO e o pedido ainda falta, entao ele vira o primeiro campo da propria caixa.
   *
   * ⚠️ Era um modal separado ate 08/09/2026 e ele pediu uma tela so'. Duas caixas em sequencia pro
   * mesmo ato e' o gesto partido no meio: a primeira nao mostra o que a segunda vai pedir, e foi
   * assim que ele nao achou o campo de comentario que ja existia.
   */
  linha: PedidoDoDesfecho | null;
  tipo: 'perdido' | 'adiado'; ehAdmin: boolean;
  /** Dia e hora ja escolhidos — chegam do duplo-clique na Agenda. */
  dataInicial?: string; horaInicial?: string;
  /**
   * O compromisso que esta sendo EDITADO ([DA-684]) — pedido dele em 08/09/2026: *"gostaria de
   * poder Editar o agendamento"* e *"poder Excluir, principalmente para casos de marcar errado"*.
   *
   * ⚠️ Presente, a caixa deixa de CRIAR e passa a corrigir: grava por `PUT /andamentos/:id` em vez
   * de abrir uma linha nova, e ganha o botao Excluir. Uma caixa so' pros tres gestos (marcar da
   * fila, marcar da agenda, editar) — a mesma decisao de "uma tela so'" desta manha.
   */
  andamentoId?: number;
  situacaoInicial?: number | null;
  comentarioInicial?: string | null;
  onFechar: () => void; onGravado: () => void;
}) {
  // O pedido em que este desfecho vai ser gravado. Nasce do que veio de fora (fluxo da fila) e so'
  // e' escolhido aqui dentro no fluxo da Agenda.
  const [pedido, setPedido] = useState<PedidoDoDesfecho | null>(linha);
  const toast = useToast();
  const dialog = useDialog();
  const [motivos, setMotivos] = useState<MotivoPerda[]>([]);
  // Qual rotulo esta com a paleta aberta, e qual dos dois campos. Um so de cada vez: duas tiras
  // abertas competiriam pelo mesmo espaco embaixo da grade.
  const [paleta, setPaleta] = useState<{ id: number; campo: CampoCor } | null>(null);
  const [carregandoMotivos, setCarregandoMotivos] = useState(tipo === 'perdido');
  const [motivoId, setMotivoId] = useState<number | null>(null);
  const [comentario, setComentario] = useState(comentarioInicial ?? '');
  const [novoMotivo, setNovoMotivo] = useState(false);
  const [editandoMotivo, setEditandoMotivo] = useState<{ id: number; nome: string } | null>(null);
  const [salvando, setSalvando] = useState(false);

  // --- Andamento ([DA-672]) ---
  const [situacoes, setSituacoes] = useState<SituacaoAndamento[]>([]);
  const [carregandoSituacoes, setCarregandoSituacoes] = useState(tipo === 'adiado');
  // No modo edicao ja nasce com a situacao gravada — a caixa tem que abrir mostrando o que ESTA
  // marcado, senao editar um comentario obrigaria a reescolher o rotulo do zero.
  const [situacaoId, setSituacaoId] = useState<number | null>(situacaoInicial ?? null);
  const [novaSituacao, setNovaSituacao] = useState(false);
  const [editandoSituacao, setEditandoSituacao] = useState<{ id: number; nome: string } | null>(null);
  // A proposta e' a proxima hora cheia — o palpite que quase sempre esta perto e sempre e' redondo
  // (mesma escolha da Agenda do AppCel). Depois das 18h ele nao serve: propoe amanha as 08:00, que
  // e' quando alguem de fato vai ligar.
  //
  // ⚠️ **O palpite so vale quando NINGUEM escolheu ainda.** Vindo do duplo-clique na Agenda, o dia e
  // a hora sao a celula que a pessoa clicou — sobrescrever isso com "a proxima hora cheia" jogaria
  // fora o unico dado que aquele gesto carrega, e o campo abriria contradizendo o clique.
  const [dataAndamento, setDataAndamento] = useState(() => {
    if (dataInicial) return dataInicial;
    const d = new Date();
    if (d.getHours() >= 18) d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [horaAndamento, setHoraAndamento] = useState(() => {
    if (horaInicial) return horaInicial;
    const d = new Date();
    return d.getHours() >= 18 ? '08:00' : `${String(d.getHours() + 1).padStart(2, '0')}:00`;
  });

  // A lista vem do cadastro ([DA-671]) — a tela nunca a escreve a mao, senao motivo criado pelo
  // admin nao apareceria aqui e o cadastro seria enfeite.
  useEffect(() => {
    if (tipo !== 'perdido') return;
    let vivo = true;
    api.get<{ linhas: MotivoPerda[] }>('/api/motivos-perda')
      .then((r) => { if (vivo) setMotivos(r.linhas); })
      .catch((e: any) => toast.erro(e?.message ?? 'Falha ao carregar os motivos'))
      .finally(() => { if (vivo) setCarregandoMotivos(false); });
    return () => { vivo = false; };
  }, [tipo, toast]);

  // A lista de situacoes vem do cadastro, igual a de motivos — a tela nunca a escreve a mao.
  useEffect(() => {
    if (tipo !== 'adiado') return;
    let vivo = true;
    api.get<{ linhas: SituacaoAndamento[] }>('/api/situacoes-andamento')
      .then((r) => { if (vivo) setSituacoes(r.linhas); })
      .catch((e: any) => toast.erro(e?.message ?? 'Falha ao carregar as situacoes'))
      .finally(() => { if (vivo) setCarregandoSituacoes(false); });
    return () => { vivo = false; };
  }, [tipo, toast]);

  const escolhido = motivos.find((m) => m.id === motivoId) ?? null;
  const situacaoEscolhida = situacoes.find((s) => s.id === situacaoId) ?? null;
  // Piso do seletor de data. Local, nao `toISOString()`: o ISO e' UTC, e das 21h em diante ele ja
  // devolve o dia SEGUINTE — o campo abriria com o dia de hoje bloqueado.
  const hojeISO = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();
  const faltaComentario = !!escolhido?.exige_comentario && comentario.trim().length < 3;

  // ⚠️ A fronteira e' o INSTANTE, nao mais o dia: com a hora no compromisso, marcar pra hoje as
  // 16h as 14h e' o caso mais comum de todos, e a regra antiga ("depois de hoje") o recusava.
  // `new Date('2026-09-08T14:00')` sem fuso e' lido no relogio DESTA maquina — e' o que se quer
  // aqui, porque quem digita e o relogio da comparacao sao o mesmo. Quem monta o instante que vai
  // pro banco e' o SERVIDOR, no fuso da loja.
  const instanteEscolhido = /^\d{4}-\d{2}-\d{2}$/.test(dataAndamento) && /^\d{2}:\d{2}$/.test(horaAndamento)
    ? new Date(`${dataAndamento}T${horaAndamento}`)
    : null;
  const jaPassou = !!instanteEscolhido && instanteEscolhido.getTime() <= Date.now();

  // Qual rotulo a tira esta pintando — DERIVADO da lista, nunca copiado pro estado da paleta.
  // Guardar nome e cor junto do id os deixaria envelhecer: a cor muda a cada clique na paleta, e a
  // tira mostraria o valor de antes como se fosse o escolhido.
  const listaDaPaleta: Array<{ id: number; nome: string; cor_fundo?: string | null; cor_fonte?: string | null }> =
    tipo === 'perdido' ? motivos : situacoes;
  const alvoDaPaleta = paleta ? listaDaPaleta.find((x) => x.id === paleta.id) : undefined;
  const paletaAberta = paleta && alvoDaPaleta
    ? {
      id: alvoDaPaleta.id,
      nome: alvoDaPaleta.nome,
      campo: paleta.campo,
      valor: paleta.campo === 'fundo' ? alvoDaPaleta.cor_fundo : alvoDaPaleta.cor_fonte,
    }
    : null;

  const impedimento = !pedido
    ? 'Escolha de qual pedido e o compromisso.'
    : tipo === 'perdido'
    ? (carregandoMotivos ? 'Carregando os motivos...'
      : motivos.length === 0 ? 'Nenhum motivo cadastrado — avise o administrador.'
      : !escolhido ? 'Escolha o motivo da perda.'
      : faltaComentario ? 'Este motivo pede uma explicacao curta.'
      : null)
    : (carregandoSituacoes ? 'Carregando as situacoes...'
      : situacoes.length === 0 ? 'Nenhuma situacao cadastrada — avise o administrador.'
      : !situacaoEscolhida ? 'Escolha a situacao do andamento.'
      : !instanteEscolhido ? 'Informe o dia e a hora do proximo contato.'
      : jaPassou ? 'O proximo contato tem que ser no futuro.'
      : null);

  // Pintar dali mesmo, pelo mesmo argumento do renomear: ele esta com a lista na frente e e' ali
  // que a cor faz falta. Grava na TABELA — a cor e' do cadastro, nao um enfeite desta tela, e e'
  // por isso que ela vale na coluna, na Agenda e pra quem mais desenhar o rotulo ([DA-674]).
  //
  // ⚠️ Uma unica funcao pras duas listas: escrever duas ja falha por definicao — quando uma mudar,
  // a outra fica pra tras. A rota certa sai do proprio tipo do modal.
  async function salvarCor(id: number, campo: CampoCor, cor: string | null) {
    const rota = tipo === 'perdido' ? '/api/motivos-perda' : '/api/situacoes-andamento';
    const corpo = campo === 'fundo' ? { cor_fundo: cor } : { cor_fonte: cor };
    try {
      const novo = await api.put<{ id: number; cor_fundo: string | null; cor_fonte: string | null }>(`${rota}/${id}`, corpo);
      const aplicar = <T extends { id: number }>(lista: T[]) =>
        lista.map((x) => (x.id === novo.id ? { ...x, cor_fundo: novo.cor_fundo, cor_fonte: novo.cor_fonte } : x));
      if (tipo === 'perdido') setMotivos((atual) => aplicar(atual));
      else setSituacoes((atual) => aplicar(atual));
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gravar a cor');
    }
  }

  // Renomear dali mesmo: ele esta com a lista na frente e o nome ruim aparece na hora de usar.
  // Grava na tabela de motivos, entao vale pra todo mundo — nao e' um apelido desta tela.
  async function renomearMotivo() {
    if (!editandoMotivo) return;
    const nome = editandoMotivo.nome.trim();
    const antes = motivos.find((m) => m.id === editandoMotivo.id)?.nome;
    setEditandoMotivo(null);
    if (nome.length < 3 || nome === antes) return; // nada mudou: nao gasta chamada nem toast
    try {
      const novo = await api.put<MotivoPerda>(`/api/motivos-perda/${editandoMotivo.id}`, { nome });
      setMotivos((atual) => atual.map((m) => (m.id === novo.id ? { ...m, nome: novo.nome } : m)));
      toast.sucesso(`Agora chama ${novo.nome}`);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao renomear');
    }
  }

  // Renomear a situacao dali mesmo — irmao do renomear do motivo, e pela mesma razao: ele esta
  // com a lista na frente e o nome ruim aparece na hora de usar. Grava na tabela, vale pra todos.
  async function renomearSituacao() {
    if (!editandoSituacao) return;
    const nome = editandoSituacao.nome.trim();
    const antes = situacoes.find((s) => s.id === editandoSituacao.id)?.nome;
    setEditandoSituacao(null);
    if (nome.length < 3 || nome === antes) return;
    try {
      const novo = await api.put<SituacaoAndamento>(`/api/situacoes-andamento/${editandoSituacao.id}`, { nome });
      setSituacoes((atual) => atual.map((s) => (s.id === novo.id ? { ...s, nome: novo.nome } : s)));
      toast.sucesso(`Agora chama ${novo.nome}`);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao renomear');
    }
  }

  /**
   * Excluir o compromisso — *"principalmente para casos de marcar errado"* (dele, 08/09/2026).
   *
   * ⚠️ **Confirma antes, e a pergunta diz a CONSEQUENCIA, nao so' "tem certeza?"**: apagar o
   * compromisso mais recente devolve o pedido pra fila sem data marcada, e quem clica precisa saber
   * disso ANTES. A tela nao tem como saber se aquele e' o mais recente (a grade mostra a semana,
   * nao o historico), entao a frase cobre o caso: e' o servidor que decide, e ele avisa na resposta.
   */
  async function excluir() {
    if (!andamentoId || salvando) return;
    const ok = await dialog.confirmar(
      'Excluir este compromisso? Se ele for o mais recente do pedido, o pedido volta pra fila sem data marcada.',
      { titulo: 'Excluir compromisso', textoConfirmar: 'Excluir', perigoso: true },
    );
    if (!ok) return;
    setSalvando(true);
    try {
      const r = await api.delete<{ voltou_pra_fila?: boolean }>(`/api/fila-retomada/andamentos/${andamentoId}`);
      toast.sucesso(r?.voltou_pra_fila
        ? 'Compromisso excluido — o pedido voltou pra fila'
        : 'Compromisso excluido');
      onGravado();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao excluir');
    } finally {
      setSalvando(false);
    }
  }

  async function gravar() {
    // `!pedido` ja e' o primeiro `impedimento`, entao este `return` nunca dispara sozinho — ele
    // existe pra estreitar o tipo sem `!`, que e' uma promessa que o compilador acredita.
    if (impedimento || salvando || !pedido) return;
    setSalvando(true);
    // EDITANDO: corrige a linha que ja existe. ⛔ Nunca `POST /desfecho` aqui — aquele abre uma
    // linha NOVA, e a correcao viraria um segundo compromisso com o primeiro errado ainda de pe.
    if (andamentoId) {
      try {
        await api.put(`/api/fila-retomada/andamentos/${andamentoId}`, {
          situacao_id: situacaoId,
          data: dataAndamento,
          hora: horaAndamento,
          comentario: comentario.trim() || undefined,
        });
        toast.sucesso(`${pedido.numero} agora em ${dataAndamento.split('-').reverse().join('/')} as ${horaAndamento}`);
        onGravado();
      } catch (e: any) {
        toast.erro(e?.message ?? 'Falha ao gravar');
      } finally {
        setSalvando(false);
      }
      return;
    }
    try {
      await api.post(`/api/fila-retomada/${pedido.id}/desfecho`, {
        status: tipo,
        motivo_id: tipo === 'perdido' ? motivoId : undefined,
        situacao_id: tipo === 'adiado' ? situacaoId : undefined,
        // O comentario vale nos DOIS agora: na perda ele explica o motivo, no andamento ele e' o
        // caso — e' o texto que a Agenda exibe embaixo da hora.
        comentario: comentario.trim() || undefined,
        data: tipo === 'adiado' ? dataAndamento : undefined,
        hora: tipo === 'adiado' ? horaAndamento : undefined,
      });
      toast.sucesso(tipo === 'perdido'
        ? `${pedido.numero} marcado como perdido`
        : `${pedido.numero} volta em ${dataAndamento.split('-').reverse().join('/')} as ${horaAndamento}`);
      onGravado();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gravar');
      // 409 = o pedido saiu da fila porque virou venda. Recarrega pra tela parar de mostrar
      // uma linha que nao existe mais, em vez de deixar a operadora tentando de novo.
      if (e?.status === 409) onGravado();
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      compacto
      // 540 e' medido, nao escolhido: com 480 o cartao dava 184px de texto e o maior motivo
      // ('NAO TINHAMOS O PRODUTO') pede 194 — dois dos sete quebravam em duas linhas e a grade
      // ficava com cartoes de alturas diferentes. Aqui cada cartao entrega ~213px, folga de 19.
      largura={540}
      // O titulo diz o que ainda falta: sem pedido escolhido ele nomeia o COMPROMISSO (o que a
      // celula ja definiu), e passa a nomear o pedido assim que ele existe.
      titulo={tipo === 'perdido'
        ? `Perdeu a venda — ${pedido?.numero ?? ''}`
        : pedido ? `Andamento — ${pedido.numero}` : 'Marcar compromisso'}
      rodape={
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-xs" style={{ color: impedimento ? '#b45309' : 'transparent' }}>
            {impedimento ?? '.'}
          </span>
          <div className="flex gap-2">
            {/* ⚠️ Excluir e' o unico botao desta caixa que APAGA registro: fica separado do
                Confirmar e pede confirmacao com a consequencia escrita, nao um "tem certeza?". */}
            {andamentoId && (
              <button
                type="button"
                className="btn-secundario"
                style={{ color: '#b91c1c' }}
                disabled={salvando}
                onClick={() => void excluir()}
              >
                Excluir
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={onFechar}>Cancelar</button>
            <button type="button" className="btn-primario" disabled={!!impedimento || salvando} onClick={() => void gravar()}>
              {salvando ? 'Gravando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      }
    >
      {/* O campo do PEDIDO, so' no fluxo da Agenda (`linha === null`). Fica em cima porque e' a
          primeira coisa que a pessoa decide, e some numa linha de resumo depois de escolhido —
          senao a lista de 22 empurraria a situacao e o comentario pra fora da janela.
          ⚠️ O resto da caixa continua VISIVEL enquanto o pedido nao foi escolhido: era isso que ele
          pediu com "uma tela so'". O que segura a gravacao e' o `impedimento`, escrito no rodape,
          nao esconder metade dos campos. */}
      {linha === null && (
        <div className="mb-4">
          {pedido ? (
            <div className="flex items-center justify-between gap-3 text-sm rounded-lg px-3 py-2"
              style={{ backgroundColor: 'var(--grid-row-alt-bg)', color: 'var(--grid-row-fg)' }}>
              <span className="min-w-0">
                <span className="text-gray-400">Pedido: </span>
                <span className="font-medium">{pedido.cliente ?? 'Sem nome'}</span>
                <span className="text-gray-400"> · {pedido.numero} · {formatMoeda(Number(pedido.total))}</span>
              </span>
              <button type="button" className="btn-secundario text-xs" onClick={() => setPedido(null)}>
                Trocar
              </button>
            </div>
          ) : (
            <SeletorPedido onEscolher={setPedido} />
          )}
        </div>
      )}

      {tipo === 'perdido' ? (
        <>
          <label className="block text-sm font-medium mb-2">Por que foi perdida?</label>

          {carregandoMotivos ? (
            <p className="text-sm text-gray-400 py-4">Carregando os motivos...</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {motivos.map((m) => {
                const ativo = m.id === motivoId;
                const editando = editandoMotivo?.id === m.id;
                // O cartao e' uma CAIXA com dois botoes dentro, nao um botao so: botao dentro de
                // botao e' HTML invalido, e o lapis precisa de clique proprio.
                return (
                  <div
                    key={m.id}
                    className="rounded-lg transition flex items-center"
                    style={{
                      // ⚠️ **A selecao virou ANEL em 04/09/2026 ([DA-674]).** Ela era um fundo azul
                      // translucido; com cada rotulo pintado com a cor DELE, o fundo parou de poder
                      // dizer "escolhido" — duas coisas na mesma propriedade viram afirmacao falsa,
                      // nao ambiguidade ([DA-469]). Quando falta cor, procura-se FORMA: o anel
                      // compoe POR FORA, sobre qualquer par que ele escolha. Mesma saida da regua de
                      // chips do AppCel, onde isto ja aconteceu ([DA-592]).
                      ...pinturaDoRotulo(m.cor_fundo, m.cor_fonte),
                      border: '1px solid var(--border-color, #d1d5db)',
                      outline: ativo ? '3px solid #111827' : 'none',
                      outlineOffset: 1,
                      fontWeight: ativo ? 700 : 400,
                      padding: '2px',
                      // Piso de altura pra grade ficar toda do mesmo tamanho (pedido dele com o
                      // print). Nome que um dia nao couber cresce SO o cartao dele — cortar com
                      // reticencias nao serve aqui: o motivo e' o que a vendedora le pra escolher.
                      minHeight: 38,
                    }}
                  >
                    {editando ? (
                      <input
                        autoFocus
                        value={editandoMotivo.nome}
                        onChange={(e) => setEditandoMotivo({ id: m.id, nome: upperMantendoCursor(e) })}
                        onKeyDown={(e) => {
                          // O Enter aqui e' do NOME, nunca do desfecho — sem isto ele gravaria a
                          // perda com o motivo que estava selecionado atras.
                          e.stopPropagation();
                          if (e.key === 'Enter') { e.preventDefault(); void renomearMotivo(); }
                          if (e.key === 'Escape') { e.preventDefault(); setEditandoMotivo(null); }
                        }}
                        onBlur={() => void renomearMotivo()}
                        maxLength={60}
                        className="w-full text-sm px-2 py-1.5 rounded outline-none"
                        style={{ border: '1px solid #2563eb', backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setMotivoId(m.id)}
                          className="text-left text-sm px-2.5 py-1.5 flex-1 min-w-0"
                          style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit' }}
                        >
                          {m.nome}
                        </button>
                        {/* Os dois circulos (fundo, fonte) so pro admin — pedido dele, 04/09/2026.
                            A COR que eles definem aparece pra todo mundo; o que e' so-admin e'
                            escolher. */}
                        {ehAdmin && (
                          <CirculosCor
                            nome={m.nome}
                            fundo={m.cor_fundo}
                            fonte={m.cor_fonte}
                            campoAberto={paleta?.id === m.id ? paleta.campo : null}
                            onAbrir={(campo) => setPaleta(campo ? { id: m.id, campo } : null)}
                          />
                        )}
                        {/* Lapis so pro administrador, como o "+". Fica SEMPRE visivel (nao no
                            hover): no tablet nao existe hover, e o que so aparece ao passar o
                            mouse nao existe pra quem opera de dedo. */}
                        {ehAdmin && (
                          <button
                            type="button"
                            onClick={() => setEditandoMotivo({ id: m.id, nome: m.nome })}
                            title={`Ajustar o nome de ${m.nome}`}
                            aria-label={`Ajustar o nome de ${m.nome}`}
                            className="px-2 py-1.5 text-gray-400 hover:text-blue-600"
                            style={{ background: 'none', border: 'none', flex: '0 0 auto' }}
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {/* O + so aparece pro administrador (pedido dele, 04/09/2026). Tracejado pra nao se
                  confundir com motivo: ele nao e' uma escolha, e' um cadastro. */}
              {ehAdmin && (
                <button
                  type="button"
                  onClick={() => setNovoMotivo(true)}
                  className="text-left text-sm px-3 py-2 rounded-lg text-gray-500 flex items-center gap-1.5"
                  style={{ border: '1px dashed var(--border-color, #d1d5db)', backgroundColor: 'transparent', minHeight: 38 }}
                >
                  <Plus size={14} /> Novo motivo
                </button>
              )}

              {/* A tira sempre no MESMO lugar (fim da grade), nunca colada ao cartao clicado: com
                  8 motivos em duas colunas, uma tira que nasce no meio empurra metade da grade pra
                  baixo e o cartao que ele acabou de mirar muda de lugar embaixo do dedo. */}
              {paletaAberta && (
                <TiraPaleta
                  nome={paletaAberta.nome}
                  campo={paletaAberta.campo}
                  valor={paletaAberta.valor}
                  onEscolher={(cor) => void salvarCor(paletaAberta.id, paletaAberta.campo, cor)}
                  onFechar={() => setPaleta(null)}
                />
              )}
            </div>
          )}

          <label className="block text-sm font-medium mt-4 mb-1">
            Comentario {escolhido?.exige_comentario
              ? <span className="text-xs font-normal" style={{ color: '#b45309' }}>(obrigatorio para este motivo)</span>
              : <span className="text-xs font-normal text-gray-400">(opcional)</span>}
          </label>
          <input
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void gravar(); }}
            placeholder="Alguma coisa que ajude a entender o caso..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
          />
          <p className="text-xs text-gray-500 mt-2">
            E' o unico lugar do sistema que registra por que a loja perde venda. Com a lista fechada,
            em um mes da' pra somar quanto cada motivo custou.
          </p>

          {novoMotivo && (
            <ModalCadastroRapido
              titulo="Novo motivo de perda"
              endpoint="/api/motivos-perda"
              campos={[{ key: 'nome', label: 'Motivo', obrigatorio: true }]}
              onFechar={() => setNovoMotivo(false)}
              onSalvo={(novo: MotivoPerda) => {
                // Entra na lista E ja fica escolhido: quem cadastrou estava no meio de um desfecho,
                // e obrigar a procurar o proprio motivo recem-criado e' passo a toa.
                setMotivos((atual) => [...atual, novo]);
                setMotivoId(novo.id);
                setNovoMotivo(false);
              }}
            />
          )}
        </>
      ) : (
        <>
          <label className="block text-sm font-medium mb-2">Em que pe esta?</label>

          {carregandoSituacoes ? (
            <p className="text-sm text-gray-400 py-4">Carregando as situacoes...</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {situacoes.map((s) => {
                const ativo = s.id === situacaoId;
                const editando = editandoSituacao?.id === s.id;
                // Mesma forma do cartao do "Perdeu": uma CAIXA com dois botoes dentro (botao dentro
                // de botao e' HTML invalido, e o lapis precisa de clique proprio).
                return (
                  <div
                    key={s.id}
                    className="rounded-lg transition flex items-center"
                    style={{
                      // Selecao por ANEL, mesma razao do cartao do Perdeu logo acima ([DA-674]):
                      // com o rotulo pintado pela cor dele, o fundo deixou de poder dizer
                      // "escolhido".
                      ...pinturaDoRotulo(s.cor_fundo, s.cor_fonte),
                      border: '1px solid var(--border-color, #d1d5db)',
                      outline: ativo ? '3px solid #111827' : 'none',
                      outlineOffset: 1,
                      fontWeight: ativo ? 700 : 400,
                      padding: '2px',
                      minHeight: 38,
                    }}
                  >
                    {editando ? (
                      <input
                        autoFocus
                        value={editandoSituacao.nome}
                        onChange={(e) => setEditandoSituacao({ id: s.id, nome: upperMantendoCursor(e) })}
                        onKeyDown={(e) => {
                          // O Enter aqui e' do NOME, nunca do andamento — sem isto ele gravaria o
                          // compromisso com a situacao que estava selecionada atras.
                          e.stopPropagation();
                          if (e.key === 'Enter') { e.preventDefault(); void renomearSituacao(); }
                          if (e.key === 'Escape') { e.preventDefault(); setEditandoSituacao(null); }
                        }}
                        onBlur={() => void renomearSituacao()}
                        maxLength={60}
                        className="w-full text-sm px-2 py-1.5 rounded outline-none"
                        style={{ border: '1px solid #2563eb', backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setSituacaoId(s.id)}
                          className="text-left text-sm px-2.5 py-1.5 flex-1 min-w-0"
                          style={{ background: 'none', border: 'none', color: 'inherit', font: 'inherit' }}
                        >
                          {s.nome}
                        </button>
                        {ehAdmin && (
                          <CirculosCor
                            nome={s.nome}
                            fundo={s.cor_fundo}
                            fonte={s.cor_fonte}
                            campoAberto={paleta?.id === s.id ? paleta.campo : null}
                            onAbrir={(campo) => setPaleta(campo ? { id: s.id, campo } : null)}
                          />
                        )}
                        {/* Lapis so pro administrador, SEMPRE visivel (nunca no hover): no tablet
                            nao existe hover, e o que so aparece com o mouse em cima nao existe
                            pra quem opera de dedo. */}
                        {ehAdmin && (
                          <button
                            type="button"
                            onClick={() => setEditandoSituacao({ id: s.id, nome: s.nome })}
                            title={`Ajustar o nome de ${s.nome}`}
                            aria-label={`Ajustar o nome de ${s.nome}`}
                            className="px-2 py-1.5 text-gray-400 hover:text-blue-600"
                            style={{ background: 'none', border: 'none', flex: '0 0 auto' }}
                          >
                            <Pencil size={13} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {ehAdmin && (
                <button
                  type="button"
                  onClick={() => setNovaSituacao(true)}
                  className="text-left text-sm px-3 py-2 rounded-lg text-gray-500 flex items-center gap-1.5"
                  style={{ border: '1px dashed var(--border-color, #d1d5db)', backgroundColor: 'transparent', minHeight: 38 }}
                >
                  <Plus size={14} /> Nova situacao
                </button>
              )}

              {paletaAberta && (
                <TiraPaleta
                  nome={paletaAberta.nome}
                  campo={paletaAberta.campo}
                  valor={paletaAberta.valor}
                  onEscolher={(cor) => void salvarCor(paletaAberta.id, paletaAberta.campo, cor)}
                  onFechar={() => setPaleta(null)}
                />
              )}
            </div>
          )}

          {/* ⚠️ O rotulo diz o que a data FAZ, nao o que ela e'. "Reagendado" tem data obvia, mas
              "Negociacao" e "Aguardando" sao estados, e ali da' vontade de deixar em branco — e um
              pedido que sai da fila sem data de volta nao volta nunca. Nomeando o efeito, obrigar
              nas tres deixa de parecer burocracia. */}
          <label className="block text-sm font-medium mt-4 mb-1">Voltar pra fila em</label>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              autoFocus
              type="date"
              value={dataAndamento}
              min={hojeISO}
              onChange={(e) => setDataAndamento(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
            />
            <input
              type="time"
              value={horaAndamento}
              onChange={(e) => setHoraAndamento(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
            />
          </div>

          <label className="block text-sm font-medium mt-4 mb-1">
            Comentario <span className="text-xs font-normal text-gray-400">(opcional)</span>
          </label>
          <input
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void gravar(); }}
            placeholder="O que ficou combinado com o cliente..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            style={{ backgroundColor: 'var(--input-bg)', color: 'var(--input-fg)' }}
          />

          {/* ⚠️ Este texto dizia *"o pedido sai da fila e volta sozinho nessa hora"* e ficou VENCIDO
              na propria manha de 04/09 ([DA-672]): ele mandou o adiado PARAR de sumir. Texto de UI
              vencido nao e' link morto — e' instrucao que o operador obedece, e ela sobreviveu
              algumas horas a mudanca que a desmentiu ([DA-639]). */}
          <p className="text-xs text-gray-500 mt-2">
            O pedido <strong>continua na lista</strong> com a situacao escrita na caixa — o aviso do
            topo e' que para de cobrar ate a hora marcada. O compromisso aparece na aba{' '}
            <strong>Agenda</strong>, com este comentario embaixo da hora.
          </p>

          {novaSituacao && (
            <ModalCadastroRapido
              titulo="Nova situacao"
              endpoint="/api/situacoes-andamento"
              campos={[{ key: 'nome', label: 'Situacao', obrigatorio: true }]}
              onFechar={() => setNovaSituacao(false)}
              onSalvo={(novo: SituacaoAndamento) => {
                // Entra na lista E ja fica escolhida: quem cadastrou estava no meio de um
                // andamento, e obrigar a procurar a propria situacao recem-criada e' passo a toa.
                setSituacoes((atual) => [...atual, novo]);
                setSituacaoId(novo.id);
                setNovaSituacao(false);
              }}
            />
          )}
        </>
      )}
    </Modal>
  );
}
